import {
  getRangeLabel,
  buildRangeQuery,
} from "../components/discoveryRangeFilters.shared";
import {
  Button,
  CardImage,
  DialogNavigation,
  Icon,
  IconType,
  InputCheckbox,
  InputContainer,
  InputNumber,
  InputOption,
  InputSelect,
  InputSwitch,
  InputText,
  RangeSelector,
  Spinner,
  type RangePreset,
  type RangeValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TabbedDrawer,
  TabbedDrawerHeader,
  TabbedDrawerTab,
} from "@sellar-io/components";
import {
  ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { AlertContainer } from "~/components/alert/AlertContainer";
import { DialogTitle } from "@headlessui/react";
import { useProductAvailableFormats } from "~/hooks/useProductAvailableFormats";
import type { OrderRequirement } from "~/hooks/usePriceTierOrderRequirements";
import { useRemoteFeatureFlags } from "~/hooks/useRemoteFeatureFlags";
import { useProductDialogOrderRequirements } from "./useProductDialogOrderRequirements";
import { twMerge } from "tailwind-merge";
import {
  NavigationBar,
  NavigationBarSubHeader,
} from "~/components/navigation/NavigationBar";
import { DiscoveryMarketSearch } from "../components/DiscoveryMarketSearch";
import { ImageSlider } from "../../../components/imageSlider/ImageSlider";
import { OrderBySelector } from "../v2/lib/OrderBySelector";
import {
  estimateRetailServingPrice,
  getRetailServingDetails,
  formatCurrency,
  getContainerTypes,
  getDiscountDescription,
  getDisplayNameByKey,
  orderableItemValues,
  pluralize,
} from "@sellar-io/common";
import {
  Dialog,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import {
  ContainerType,
  DeliveredBySellarFilterOption,
  DietaryType,
  genericContainerTypes,
  specificContainerTypes,
  useDiscoveryV2SearchStore,
} from "../v2/DiscoveryV2SearchStore";
import { useShallow } from "zustand/shallow";
import { useDiscoveryV2ProductList } from "~/hooks/api/v2/discovery/useDiscoveryV2ProductList";
import { filterStyles } from "~/components/marketSearch/marketSearchFilters";
import { localMarketSuggestions } from "~/components/marketSearch/marketSearchSelections";
import { useLocalStorage, useMediaQuery } from "usehooks-ts";
import { useDebounce } from "use-debounce";
import { keepPreviousData } from "@tanstack/react-query";
import { useSellarUser } from "~/hooks/useSellarUser.shared";
import { useSellarCurrentBusiness } from "~/hooks/useSellarCurrentBusiness.shared";

import {
  BuyerProductGetResponseType,
  SellarDistributionAvailabilityDeliveryEstimatesResponseType,
  DiscoveryV2ListResponseType,
  SupplierStorefrontV2GetResponseType,
} from "@sellar-io/api-v2/types";
import {
  SkeletonContainer,
  SkeletonImage,
  SkeletonText,
} from "../../../components/skeleton/Skeleton";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { IntersectingInfiniteLoadingButton } from "~/components/infiniteLoading/IntersectingInfiniteLoadingButton";
import { formatPriceInputToApi } from "~/hooks/usePriceFormat";
import { useNavigate } from "react-router";
import { useProductForMarket } from "~/hooks/api/v2/product/useProductForMarket";
import { useGetUpcomingDeliveryGroups } from "../v2/lib/useGetUpcomingDeliveryGroups";
import { useDiscoverySupplierList } from "~/hooks/api/v2/discovery/useDiscoverySupplierList";
import { useBuyerRelationshipCount } from "~/hooks/api/v2/buyerRelationship/useBuyerRelationshipCount";
import { MarketBasketFeedbackProvider } from "../v2/distribution/MarketBasketFeedbackContext";
import { MarketBasketFlyover } from "../v2/distribution/MarketBasketFlyover";
import { DraftBasketDrawer } from "../v2/distribution/DraftBasketDrawer";
import {
  useConsolidatedBasket,
  type ConsolidatedBasketProduct,
} from "../v2/distribution/useConsolidatedBasket";
import { QuantityIndicator } from "~/components/quantityIndicator/QuantityIndicator";
import { useSupplier } from "~/hooks/api/v2/supplier/useSupplier";
import { useSupplierStorefrontV2 } from "~/hooks/api/v2/supplier/useSupplierStorefrontV2";
import { useUnifiedCheckoutBasketList } from "~/hooks/api/v2/unified-checkout-basket/useUnifiedCheckoutBaskets";
import { useUnifiedCheckoutDraftOrders } from "~/pages/unifiedCheckout/hooks/useUnifiedCheckoutDraftOrders";
import { calculateUnifiedCheckoutOrderPricing } from "~/pages/unifiedCheckout/lib/calculateUnifiedCheckoutOrderPricing";
import { isPromotionalOrderItem } from "~/pages/stockList/categoryPage/Storefront/StorefrontPromotion/isPromotionalOrderItem";
import { ProgressBarPurple } from "~/components/progressBar/ProgressBar";
import { useEvents, type BasketOrderItem } from "~/hooks";
import { getItemTypeTitle } from "~/components/discountSummary/lib/getItemTypeTitle";
import { useStorefrontTagList } from "~/hooks/api/v2/storefrontTags/useStorefrontTagList";
import { useCanShowDeliveredBySellarOptionV2 } from "../v2/lib/useCanShowDeliveredBySellarOptionV2";
import { useMarketStorefrontSessionId } from "~/hooks/useMarketStorefrontSessionTracking";
import { useConsolidatedOrderingSearchIntercom } from "../v2/distribution/useConsolidatedOrderingSearchIntercom";
import { getSellarWebsiteUtms } from "~/utils/utmHelper";
import { DISCOVER, ORDER_BUYER } from "~/constants/routes";
import {
  getStockDisplay,
  type StockStatus,
} from "~/components/stockIndicator/getStockDisplay";
import { useSellarDistributionAvailabilityDeliveryEstimates } from "~/hooks/api/v2/sellarDistributionAvailability/useSellarDistributionAvailabilityDeliveryEstimates";
import { useStorefrontBuyerPostcode } from "~/pages/stockList/categoryPage/Storefront/lib/useStorefrontBuyerPostcode";

dayjs.extend(advancedFormat);

type ProductDeliveryEstimate = {
  deliveryDate: string;
  orderCutoffDate: string;
};

type ProductOverlayData =
  | { type: "delivery-date"; label: string }
  | { type: "available-from"; label: string }
  | { type: "direct"; label: string };

type DiscoveryProductSelection = {
  productItemId: number;
  parentProductId: number | undefined;
};

type DiscoveryProductDetails = {
  productItemId: number;
  parentProductId: number;
  supplierId: number;
  supplierUrlId: string;
  name: string;
  variant: ProductVariant;
  style: string | undefined;
  abv: number | undefined;
  supplierName: string;
  formatLabel: string;
  price: ProductPrice;
  overlay: ProductOverlayData | undefined;
  imageUrls: string[];
  dietaryTags: ProductDietaryTag[];
  supplierLogoUrl: string;
  description: string;
  badgeImageUrl: string | undefined;
  availableStock: number;
  canBackorder: boolean;
  deliveryEstimate: ProductDeliveryEstimate | undefined;
  sku: DiscoveryProductSku;
  heroImageUrl: string;
  otherFormats: string[];
};

type DiscoveryProductBasketActions = {
  basketQuantity: number;
  canAddToBasket: boolean;
  basketActionDisabled: boolean;
  onAddToBasket: () => void;
  onIncrementQuantityInBasket: () => void;
  onDecrementQuantityInBasket: () => void;
};

type NestedCheckboxOption<T extends string> = {
  value: T;
  label: string;
  children?: readonly NestedCheckboxOption<T>[];
};

type AppliedCriterion = {
  kind: "regular" | "search" | "storefront-tag";
  id: string;
  label: string;
  onRemove: () => void;
};

type DeliveredBySellarCutoffOption = {
  value: string;
  label: string;
};

type DeliveredBySellarOption = {
  value: Exclude<DeliveredBySellarFilterOption, "all">;
  label: string;
  nestedHeader?: string;
  nestedOptions?: DeliveredBySellarCutoffOption[];
  nestedOptionsLoading?: boolean;
};

type SupplierFilterOption = {
  id: number;
  name: string;
  isMySupplier: boolean;
};

type SupplierBrowseOptionBase = {
  id: number;
  value: string | "all";
};

type SupplierBrowseOption = SupplierBrowseOptionBase & {
  label: string;
};

type SupplierVolumeDiscount = NonNullable<
  SupplierStorefrontV2GetResponseType["VolumeDiscounts"]
>["PriceTierDiscounts"][number];

type SingleSupplierCardData = {
  id: number;
  name: string;
  logoUrl: string;
  description: string;
  browseOptions: SupplierBrowseOption[];
  selectedBrowseOption: SupplierBrowseOptionBase;
  volumeDiscounts: SupplierVolumeDiscount[];
  activeVolumeDiscountIds: number[];
  orderItems: BasketOrderItem[];
  currency: string;
};

const deliveredBySellarAppliedFilterLabels: Record<
  Exclude<DeliveredBySellarFilterOption, "all">,
  string
> = {
  weekend: "Get it by this weekend",
  "next-week": "Get it by next weekend",
  "week-after-next": "Get it by the weekend after next",
};

type ProductDietaryTag = "vegan" | "lactose-free" | "gluten-free";

type AvailableProductPrice = {
  type: "fixed" | "starting-at";
  price: number;
  basePrice: number | null;
  fullPrice: number | null;
};

type ProductPrice =
  | AvailableProductPrice
  | { type: "on-request" }
  | { type: "hidden" };

type ProductVariant = "sd" | "non-sd";

type ProductSearchCardProps = {
  variant: ProductVariant;
  basketQuantity: number;
  productItemId: number;
  name: string;
  supplierName: string;
  supplierLogoUrl: string;
  availableStock: number;
  canBackorder: boolean;
  supplierUrlId: string;
  description: string;
  style: string | undefined;
  abv: number | undefined;
  price: ProductPrice;
  imageUrls: string[];
  badgeImageUrl: string | undefined;
  dietaryTags: ProductDietaryTag[];
  overlay: ProductOverlayData | undefined;
  formatLabel: string;
  deliveryEstimate: ReturnType<typeof getDiscoveryProductDeliveryEstimate>;
  parentProductId: number | undefined;
  canAddToBasket: boolean;
  basketActionDisabled: boolean;
  onAddToBasket: () => void;
  onIncrementQuantityInBasket: () => void;
  onDecrementQuantityInBasket: () => void;
  onViewProduct: () => void;
};

type DiscoveryProductSku = {
  id: number;
  name: string;
  price: ProductPrice;
  volume: number | undefined;
  containerType: string | undefined;
  formatLabel: string;
  unit: string | undefined;
  packQuantity: number | undefined;
  supplierVatPercentage: number;
  volumeDiscountTiers: { quantity: number; percentage: number }[];
  variant: ProductVariant;
};

const isSpecificContainerTypeGroup = (
  containerType: (typeof genericContainerTypes)[number],
): containerType is keyof typeof specificContainerTypes =>
  containerType in specificContainerTypes;

const getContainerTypeLabel = (containerType: ContainerType) =>
  containerType === "bagInBox"
    ? "Bag-in-box"
    : (getDisplayNameByKey(orderableItemValues, containerType) ??
      containerType
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/^./, (character) => character.toUpperCase()));

const buildStyleOptions = (): NestedCheckboxOption<string>[] =>
  Object.values(filterStyles).map((style) => {
    const children = "children" in style ? style.children : undefined;

    return {
      value: style.searchValue,
      label: style.displayValue,
      ...(children?.length && {
        children: children.map((child) => ({
          value: buildStyleSearchValue({
            categoryKey: style.key,
            categorySearchValue: style.searchValue,
            childSearchValue: child.searchValue,
          }),
          label: child.displayValue,
        })),
      }),
    };
  });

const buildStyleSearchValue = ({
  categoryKey,
  categorySearchValue,
  childSearchValue,
}: {
  categoryKey: keyof typeof filterStyles;
  categorySearchValue: string;
  childSearchValue: string;
}) => {
  const categoriesWithStandaloneChildValues: (keyof typeof filterStyles)[] = [
    "beer",
    "spirits",
    "softDrinks",
  ];

  if (categoriesWithStandaloneChildValues.includes(categoryKey)) {
    return childSearchValue;
  }

  return [childSearchValue, categorySearchValue].join(" & ");
};

const buildContainerOptions = (
  containerTypes: readonly (typeof genericContainerTypes)[number][],
): NestedCheckboxOption<ContainerType>[] =>
  containerTypes.map((containerType) => {
    const specificTypes = isSpecificContainerTypeGroup(containerType)
      ? specificContainerTypes[containerType]
      : undefined;

    return {
      value: containerType,
      label: getContainerTypeLabel(containerType),
      ...(specificTypes?.length && {
        children: specificTypes.map((specificType) => ({
          value: specificType,
          label: getContainerTypeLabel(specificType),
        })),
      }),
    };
  });

const styleOptions = buildStyleOptions();

const primaryContainerOptions = buildContainerOptions([
  "keg",
  "cask",
  "can",
  "bottle",
  "bagInBox",
]);

const secondaryContainerOptions = buildContainerOptions([
  "pouch",
  "bag",
  "jerryCan",
  "box",
]);

const containerOptions = [
  ...primaryContainerOptions,
  ...secondaryContainerOptions,
];

const dietaryOptions = [
  { value: "vegan", label: "Vegan" },
  { value: "glutenFree", label: "Gluten Free" },
  { value: "lactoseFree", label: "Lactose Free" },
] satisfies readonly NestedCheckboxOption<DietaryType>[];

const casePricePresets: readonly RangePreset[] = [
  { id: "under25", label: "Under £25", range: { type: "maximum", high: 25 } },
  {
    id: "25to50",
    label: "£25 - £50",
    range: { type: "between", low: 25, high: 50 },
  },
  {
    id: "50to75",
    label: "£50 - £75",
    range: { type: "between", low: 50, high: 75 },
  },
  { id: "over75", label: "Over £75", range: { type: "minimum", low: 75 } },
];

const unitPricePresets: readonly RangePreset[] = [
  {
    id: "under100",
    label: "Under £100",
    range: { type: "maximum", high: 100 },
  },
  {
    id: "100to125",
    label: "£100 - £125",
    range: { type: "between", low: 100, high: 125 },
  },
  {
    id: "125to150",
    label: "£125 - £150",
    range: { type: "between", low: 125, high: 150 },
  },
  { id: "over150", label: "Over £150", range: { type: "minimum", low: 150 } },
];

const lowerAbvPresets: readonly RangePreset[] = [
  {
    id: "alcoholFree",
    label: "Alcohol free (0.0 - 0.5%)",
    range: { type: "between", low: 0, high: 0.5 },
  },
  {
    id: "lowAlcohol",
    label: "Low alcohol (0.5 - 2.0%)",
    range: { type: "between", low: 0.5, high: 2 },
  },
  ...(
    [
      [2, 4],
      [4, 5],
      [5, 6],
      [6, 7],
    ] as const
  ).map(([low, high]) => ({
    id: `${low}to${high}`,
    label: `${low.toFixed(1)} - ${high.toFixed(1)}%`,
    range: { type: "between" as const, low, high },
  })),
  {
    id: "stronger",
    label: "Stronger (7.0% +)",
    range: { type: "minimum", low: 7 },
  },
];

const higherAbvPresets: readonly RangePreset[] = [
  {
    id: "lowAlcohol",
    label: "Low alcohol (0.0 - 10.0%)",
    range: { type: "between", low: 0, high: 10 },
  },
  ...(
    [
      [10, 20],
      [20, 30],
      [30, 40],
      [40, 50],
    ] as const
  ).map(([low, high]) => ({
    id: `${low}to${high}`,
    label: `${low.toFixed(1)} - ${high.toFixed(1)}%`,
    range: { type: "between" as const, low, high },
  })),
  {
    id: "stronger",
    label: "Stronger (50.0% +)",
    range: { type: "minimum", low: 50 },
  },
];

const noRangePresets: readonly RangePreset[] = [];

const getPricePresets = (types: readonly ContainerType[]) => {
  if (!types.length) {
    return noRangePresets;
  }

  if (types.every((type) => type === "can" || type === "bottle")) {
    return casePricePresets;
  }

  const unitTypes: readonly ContainerType[] = [
    "keg",
    "cask",
    ...specificContainerTypes.keg,
    ...specificContainerTypes.cask,
  ];

  if (types.every((type) => unitTypes.includes(type))) {
    return unitPricePresets;
  }

  return noRangePresets;
};

const getPriceFilterTitle = (types: readonly ContainerType[]) => {
  const presets = getPricePresets(types);

  if (presets === casePricePresets) {
    return "Price per case";
  }

  if (presets === unitPricePresets) {
    return "Price per unit";
  }

  return "Price";
};

const getAbvPresets = (styles: readonly string[]) => {
  if (!styles.length) {
    return noRangePresets;
  }

  const belongsTo = (keys: readonly string[]) =>
    styles.every((value) =>
      styleOptions.some(
        (option) =>
          keys.includes(option.label) &&
          getOptionValues(option).includes(value),
      ),
    );

  if (belongsTo(["Beer", "Cider"])) {
    return lowerAbvPresets;
  }

  if (belongsTo(["Wine", "Spirits"])) {
    return higherAbvPresets;
  }

  return noRangePresets;
};

const mapRange = (
  range: RangeValue | undefined,
  convert: (value: number) => number,
): RangeValue | undefined => {
  if (!range) {
    return undefined;
  }
  switch (range.type) {
    case "minimum":
      return { type: "minimum", low: convert(range.low) };
    case "maximum":
      return { type: "maximum", high: convert(range.high) };
    case "between":
      return {
        type: "between",
        low: convert(range.low),
        high: convert(range.high),
      };
  }
};

/**
 * Keep data flowing top-down. Hooks and managers should own
 * data fetching, state, and processing, while components remain presentational.
 *
 * When adding functionality, first identify its data and UI constraints, then
 * separate those concerns to preserve the existing flow. Prefer encapsulating
 * non-presentational logic in a hook or manager and passing its result down.
 */
export const DiscoveryV3Search = () => (
  <MarketBasketFeedbackProvider onViewBasket={() => undefined}>
    <DiscoveryV3SearchContent />
  </MarketBasketFeedbackProvider>
);

const DiscoveryV3SearchContent = () => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const basket = useConsolidatedBasket();
  const canUseBasket = Boolean(
    user && business?.Retailer?.id && basket.enabled,
  );
  const {
    selectedProduct,
    isProductDialogOpen,
    openProductDialog,
    closeProductDialog,
    clearProductSelection,
  } = useDiscoveryProductSelection();

  const [filtersDrawerOpen, setFiltersDrawerOpen] = useState(false);
  const canShowDeliveredBySellar = useCanShowDeliveredBySellarOptionV2();
  const {
    appliedCriteria,
    regularFilters,
    productList,
    containerTypes,
    styles,
    dietaryTypes,
    deliveredBySellarFilter,
    suppliersFilter,
    singleSupplierCard,
    isInStockItemsOnly,
    isAvailableToPreOrder,
    abvRange,
    priceRange,
    searchValue,
    rawQuery: rawProductListQuery,
    setPriceRange,
    setIsInStockItemsOnly,
    setIsAvailableToPreOrder,
    setDietaryTypes,
    setDeliveredBySellarActive,
    setDeliveredBySellarCutoff,
    setDeliveredBySellarOption,
    setMySuppliersOnly,
    setSupplierSearch,
    toggleSupplier,
    clearSuppliers,
    enableSingleSupplier,
    removeSingleSupplier,
    setSelectedSupplierBrowseOption,
    setStyles,
    setContainerTypes,
    setAbvRange,
    setSearchValue,
    trackSearchSubmitted,
    trackSearchViewed,
    trackSearchFilterChanged,
    onClickBack,
  } = useDiscoveryV3SearchProductListManager();

  // On first load, track search viewed if delivered by sellar is active
  useEffect(() => {
    if (!deliveredBySellarFilter.active || rawProductListQuery.isFetching) {
      return;
    }

    trackSearchViewed();
  }, [
    deliveredBySellarFilter.active,
    rawProductListQuery.isFetching,
    trackSearchViewed,
  ]);

  const clearAllFilters = () => {
    setSearchValue("");
    setIsInStockItemsOnly(true);
    setIsAvailableToPreOrder(false);
    setStyles([]);
    setContainerTypes([]);
    setPriceRange(undefined);
    setAbvRange(undefined);
    setDietaryTypes([]);
    setDeliveredBySellarActive(false);
    clearSuppliers();
  };

  const withSearchFilterTracking =
    <T extends unknown[]>(fn: (...args: T) => void) =>
    (...args: T) => {
      trackSearchFilterChanged();
      return fn(...args);
    };

  const filterProps: DiscoveryFiltersProps = {
    abvRange,
    canShowDeliveredBySellar,
    containerTypes,
    deliveredBySellarFilter,
    dietaryTypes,
    isInStockItemsOnly,
    isAvailableToPreOrder,
    priceRange,
    styles,
    suppliersFilter,
    clearSuppliers: withSearchFilterTracking(clearSuppliers),
    setAbvRange: withSearchFilterTracking(setAbvRange),
    setContainerTypes: withSearchFilterTracking(setContainerTypes),
    setDeliveredBySellarActive: withSearchFilterTracking(
      setDeliveredBySellarActive,
    ),
    setDeliveredBySellarCutoff: withSearchFilterTracking(
      setDeliveredBySellarCutoff,
    ),
    setDeliveredBySellarOption: withSearchFilterTracking(
      setDeliveredBySellarOption,
    ),
    setDietaryTypes: withSearchFilterTracking(setDietaryTypes),
    setIsInStockItemsOnly: withSearchFilterTracking(setIsInStockItemsOnly),
    setIsAvailableToPreOrder: withSearchFilterTracking(
      setIsAvailableToPreOrder,
    ),
    setMySuppliersOnly: withSearchFilterTracking(setMySuppliersOnly),
    setPriceRange: withSearchFilterTracking(setPriceRange),
    setStyles: withSearchFilterTracking(setStyles),
    setSupplierSearch: withSearchFilterTracking(setSupplierSearch),
    toggleSupplier: withSearchFilterTracking(toggleSupplier),
  };

  const products = productList.map((product) => {
    const card = buildDiscoveryV3SearchProduct({
      product,
      deliveredBySellarFilterApplied: deliveredBySellarFilter.active,
      canRequestPricing: Boolean(business && user),
    });
    const basketQuantity = basket.getItemQuantity(product.id);

    return {
      ...card,
      basketQuantity,
      canAddToBasket: card.variant === "sd" && canUseBasket,
      basketActionDisabled:
        !basket.isItemEnabled(product) || basket.isItemMutating(product.id),
      onAddToBasket: () => basket.addItemToBasket(product),
      onIncrementQuantityInBasket: () =>
        basket.updateItemQuantity(product, basketQuantity + 1),
      onDecrementQuantityInBasket: () =>
        basket.updateItemQuantity(product, basketQuantity - 1),
      onViewProduct: () => {
        openProductDialog({
          productItemId: product.id,
          parentProductId: product.Parent?.id,
        });
      },
    };
  });

  const hasLoadedSearchResults = rawProductListQuery.data !== undefined;
  const isLoadingSearchResults =
    rawProductListQuery.isPending && !hasLoadedSearchResults;
  const hasSearchResults = products.length > 0;

  return (
    <>
      <SearchHeader
        appliedFilterCount={regularFilters.length}
        onOpenFilters={() => setFiltersDrawerOpen(true)}
        basketEnabled={canUseBasket}
        basketQuantity={basket.totalItemCount}
        onOpenBasket={() => basket.openBasket("navbar")}
        searchValue={searchValue}
        onSubmitSearch={(searchValue) => {
          setSearchValue(searchValue);
          trackSearchSubmitted(searchValue);
        }}
        onCommitSearch={trackSearchSubmitted}
        onClickLeftIcon={onClickBack}
      />
      <div className="mt-4 flex min-h-0 flex-1 flex-col px-4">
        <main className="mx-auto flex w-full max-w-300 flex-1 flex-col gap-4 md:px-0 lg:px-4">
          <div className="hidden items-center justify-between gap-4 lg:flex">
            <h1 className="font-medium">Products matching your search</h1>
            <OrderBySelector
              label="Sort:"
              labelClassName="text-zinc-500"
              className="rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm leading-4.5 font-medium shadow-[0px_2px_2px_0px_rgba(0,0,0,0.15)] hover:bg-zinc-100 hover:stroke-zinc-300 hover:text-zinc-800"
            />
          </div>

          <div className="flex min-h-0 flex-1 gap-1">
            <aside className="sticky top-4 hidden max-h-[calc(100vh-2rem)] w-64 max-w-64 min-w-64 flex-col self-start overflow-y-auto pr-4 lg:flex">
              <p className="border-b border-gray-200 py-0 pb-4 text-xs font-semibold text-zinc-500 uppercase">
                Filters
              </p>
              <DiscoveryFilters {...filterProps} />
            </aside>

            <section className="flex min-w-0 flex-1 flex-col gap-4">
              <AppliedFiltersRow
                filters={appliedCriteria.filter(
                  (criterion) => criterion.kind !== "storefront-tag",
                )}
                onClearAll={clearAllFilters}
              />
              {singleSupplierCard && (
                <SingleSupplierCard
                  {...singleSupplierCard}
                  key={singleSupplierCard.id}
                  onBrowseOptionChange={setSelectedSupplierBrowseOption}
                  onRemove={removeSingleSupplier}
                />
              )}
              {isLoadingSearchResults && <LoadingSearchResultsView />}
              {hasLoadedSearchResults && hasSearchResults && (
                <SearchResultsView
                  rawProductListQuery={rawProductListQuery}
                  products={products}
                />
              )}
              {hasLoadedSearchResults && !hasSearchResults && (
                <NoSearchResultsView appliedCriteria={appliedCriteria} />
              )}
            </section>
          </div>
        </main>
        <MobileFiltersDrawer
          active={filtersDrawerOpen}
          filters={filterProps}
          onClearAll={clearAllFilters}
          setActive={setFiltersDrawerOpen}
        />
        {canUseBasket && (
          <DraftBasketDrawer
            active={basket.basketOpen}
            setActive={(active) =>
              active ? basket.openBasket("navbar") : basket.closeBasket()
            }
          />
        )}
      </div>
      <DiscoveryProductDialog
        active={isProductDialogOpen}
        selectedProduct={selectedProduct}
        searchProduct={productList.find(
          (product) => product.id === selectedProduct?.productItemId,
        )}
        isDeliveredBySellarSearch={deliveredBySellarFilter.active}
        basket={basket}
        onDismiss={closeProductDialog}
        afterLeave={clearProductSelection}
        onShowSupplierProducts={(supplier) => {
          trackSearchFilterChanged();
          enableSingleSupplier(supplier);
        }}
      />
    </>
  );
};

type DiscoveryFiltersProps = {
  abvRange: RangeValue | undefined;
  canShowDeliveredBySellar: boolean;
  containerTypes: ContainerType[];
  deliveredBySellarFilter: {
    active: boolean;
    options: DeliveredBySellarOption[];
    selectedCutoff: string;
    selectedOption: DeliveredBySellarFilterOption;
  };
  dietaryTypes: DietaryType[];
  isInStockItemsOnly: boolean;
  isAvailableToPreOrder: boolean;
  priceRange: RangeValue | undefined;
  styles: string[];
  suppliersFilter: {
    options: SupplierFilterOption[];
    selectedSupplierIds: number[];
    searchValue: string;
    isMySuppliersOnly: boolean;
    mySuppliersCount: number;
    isLoading: boolean;
  };
  clearSuppliers: () => void;
  setAbvRange: (value: RangeValue | undefined) => void;
  setContainerTypes: (values: ContainerType[]) => void;
  setDeliveredBySellarActive: (active: boolean) => void;
  setDeliveredBySellarCutoff: (cutoff: string) => void;
  setDeliveredBySellarOption: (
    option: Exclude<DeliveredBySellarFilterOption, "all">,
  ) => void;
  setDietaryTypes: (values: DietaryType[]) => void;
  setIsInStockItemsOnly: (active: boolean) => void;
  setIsAvailableToPreOrder: (active: boolean) => void;
  setMySuppliersOnly: (active: boolean) => void;
  setPriceRange: (value: RangeValue | undefined) => void;
  setStyles: (values: string[]) => void;
  setSupplierSearch: (search: string) => void;
  toggleSupplier: (supplier: SupplierFilterOption) => void;
};

const DiscoveryFilters = ({
  abvRange,
  canShowDeliveredBySellar,
  containerTypes,
  deliveredBySellarFilter,
  dietaryTypes,
  isInStockItemsOnly,
  isAvailableToPreOrder,
  priceRange,
  styles,
  suppliersFilter,
  clearSuppliers,
  setAbvRange,
  setContainerTypes,
  setDeliveredBySellarActive,
  setDeliveredBySellarCutoff,
  setDeliveredBySellarOption,
  setDietaryTypes,
  setIsInStockItemsOnly,
  setIsAvailableToPreOrder,
  setMySuppliersOnly,
  setPriceRange,
  setStyles,
  setSupplierSearch,
  toggleSupplier,
}: DiscoveryFiltersProps) => (
  <div className="flex flex-col divide-y divide-gray-200">
    <div className="flex flex-col gap-4 py-4">
      {canShowDeliveredBySellar && (
        <DeliveredBySellarFilter
          active={deliveredBySellarFilter.active}
          onActiveChange={setDeliveredBySellarActive}
          onCutoffChange={setDeliveredBySellarCutoff}
          onOptionChange={setDeliveredBySellarOption}
          options={deliveredBySellarFilter.options}
          selectedCutoff={deliveredBySellarFilter.selectedCutoff}
          selectedOption={deliveredBySellarFilter.selectedOption}
        />
      )}
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-zinc-700">Available to pre-order</span>
        <InputSwitch
          checked={isAvailableToPreOrder}
          className="relative right-1 data-checked:bg-[#A34FD2] data-checked:hover:bg-[#A34FD2] data-checked:focus:ring-[#A34FD2]"
          onChange={setIsAvailableToPreOrder}
        />
      </div>
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="text-zinc-700">Include out of stock items</span>
        <InputSwitch
          checked={!isInStockItemsOnly}
          className="relative right-1 data-checked:bg-[#A34FD2] data-checked:hover:bg-[#A34FD2] data-checked:focus:ring-[#A34FD2]"
          onChange={(includeOutOfStock) =>
            setIsInStockItemsOnly(!includeOutOfStock)
          }
        />
      </div>
    </div>

    <FilterContainer
      defaultExpanded={containerTypes.length > 0}
      title="Container"
      isApplied={containerTypes.length > 0}
      onClear={() => setContainerTypes([])}
    >
      <NestedCheckboxSelector
        onChange={setContainerTypes}
        options={primaryContainerOptions}
        secondaryOptions={secondaryContainerOptions}
        selectedValues={containerTypes}
      />
    </FilterContainer>
    <FilterContainer
      defaultExpanded={styles.length > 0}
      title="Style"
      isApplied={styles.length > 0}
      onClear={() => setStyles([])}
    >
      <NestedCheckboxSelector
        onChange={setStyles}
        options={styleOptions}
        selectedValues={styles}
      />
    </FilterContainer>
    <SuppliersFilter
      isLoading={suppliersFilter.isLoading}
      isMySuppliersOnly={suppliersFilter.isMySuppliersOnly}
      mySuppliersCount={suppliersFilter.mySuppliersCount}
      onClear={clearSuppliers}
      onMySuppliersOnlyChange={setMySuppliersOnly}
      onSearchChange={setSupplierSearch}
      onSupplierToggle={toggleSupplier}
      options={suppliersFilter.options}
      searchValue={suppliersFilter.searchValue}
      selectedSupplierIds={suppliersFilter.selectedSupplierIds}
    />
    <FilterContainer
      defaultExpanded={Boolean(priceRange)}
      title={getPriceFilterTitle(containerTypes)}
      isApplied={Boolean(priceRange)}
      onClear={() => setPriceRange(undefined)}
    >
      <RangeSelector
        key={getPriceFilterTitle(containerTypes)}
        label="Price"
        errorClassName="text-zinc-600"
        prefix="£"
        presets={getPricePresets(containerTypes)}
        value={mapRange(priceRange, (value) => value / 100)}
        onChange={(value) =>
          setPriceRange(
            mapRange(value, (bound) => formatPriceInputToApi(bound) ?? 0),
          )
        }
      />
    </FilterContainer>
    <FilterContainer
      defaultExpanded={Boolean(abvRange)}
      title="ABV"
      isApplied={Boolean(abvRange)}
      onClear={() => setAbvRange(undefined)}
    >
      <RangeSelector
        key={getAbvPresets(styles)
          .map((preset) => preset.label)
          .join("|")}
        label="ABV"
        errorClassName="text-zinc-600"
        suffix="%"
        max={100}
        presets={getAbvPresets(styles)}
        value={abvRange}
        onChange={setAbvRange}
      />
    </FilterContainer>
    <FilterContainer
      defaultExpanded={dietaryTypes.length > 0}
      title="Dietary"
      isApplied={dietaryTypes.length > 0}
      onClear={() => setDietaryTypes([])}
    >
      <NestedCheckboxSelector
        onChange={setDietaryTypes}
        options={dietaryOptions}
        selectedValues={dietaryTypes}
      />
    </FilterContainer>
  </div>
);

const MobileFiltersDrawer = ({
  active,
  filters,
  onClearAll,
  setActive,
}: {
  active: boolean;
  filters: DiscoveryFiltersProps;
  onClearAll: () => void;
  setActive: (active: boolean) => void;
}) => (
  <TabbedDrawer
    active={active}
    containerClassName="justify-start data-closed:-translate-x-full"
    hideCloseIcon
    initTab={0}
    panelClassName="w-67 max-w-[calc(100vw-3rem)]"
    setActive={setActive}
  >
    <TabbedDrawerHeader className="rounded-none border-b border-zinc-300 bg-white px-4 py-3">
      <div className="flex items-center justify-between gap-4 text-xs font-medium uppercase">
        <span>Filters</span>
        <button
          className="cursor-pointer text-purple-600 normal-case"
          onClick={onClearAll}
          type="button"
        >
          Clear all
        </button>
      </div>
    </TabbedDrawerHeader>
    <TabbedDrawerTab className="px-4" tab={0}>
      <DiscoveryFilters {...filters} />
    </TabbedDrawerTab>
  </TabbedDrawer>
);

const SuppliersFilter = ({
  options,
  selectedSupplierIds,
  searchValue,
  isMySuppliersOnly,
  mySuppliersCount,
  isLoading,
  onSearchChange,
  onMySuppliersOnlyChange,
  onSupplierToggle,
  onClear,
}: {
  options: SupplierFilterOption[];
  selectedSupplierIds: number[];
  searchValue: string;
  isMySuppliersOnly: boolean;
  mySuppliersCount: number;
  isLoading: boolean;
  onSearchChange: (search: string) => void;
  onMySuppliersOnlyChange: (selected: boolean) => void;
  onSupplierToggle: (supplier: SupplierFilterOption) => void;
  onClear: () => void;
}) => {
  const hasAppliedFilter = isMySuppliersOnly || selectedSupplierIds.length > 0;

  return (
    <FilterContainer
      defaultExpanded={hasAppliedFilter}
      title="Supplier"
      isApplied={hasAppliedFilter}
      onClear={onClear}
    >
      <div className="flex flex-col gap-3">
        <button
          aria-pressed={isMySuppliersOnly}
          className={twMerge(
            "flex w-fit cursor-pointer items-center gap-2 rounded-full border px-2.5 py-1.25 text-[13px] font-medium",
            isMySuppliersOnly
              ? "border-purple-300 bg-purple-100 text-purple-800"
              : "border-zinc-200 text-zinc-500",
          )}
          onClick={() => onMySuppliersOnlyChange(!isMySuppliersOnly)}
          type="button"
        >
          {isMySuppliersOnly ? (
            <Icon icon="STAR" size={0.6} />
          ) : (
            <Icon icon="STAR_OUTLINE" size={0.6} />
          )}
          <span className="text-zinc-700">
            My suppliers{mySuppliersCount ? ` (${mySuppliersCount})` : ""}
          </span>
        </button>

        <InputContainer className="h-9 bg-white">
          <Icon className="text-zinc-500" icon="MAGNIFY" size={0.7} />
          <InputText
            className="bg-transparent text-sm text-zinc-500 placeholder:text-zinc-400"
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search suppliers..."
            type="search"
            value={searchValue}
          />
        </InputContainer>

        <div className="flex max-h-64 flex-col gap-2 overflow-y-auto pr-3">
          {isLoading && (
            <SkeletonContainer className="flex-col gap-2">
              <SkeletonText className="h-4 w-4/5" />
              <SkeletonText className="h-4 w-3/5" />
              <SkeletonText className="h-4 w-3/4" />
            </SkeletonContainer>
          )}
          {!isLoading &&
            options.map((supplier) => (
              <label
                className="flex cursor-pointer items-center gap-2 text-sm"
                key={supplier.id}
              >
                <InputCheckbox
                  checked={selectedSupplierIds.includes(supplier.id)}
                  className="h-4 w-4 rounded bg-white accent-purple-600 [&:not(:checked)]:appearance-none [&:not(:checked)]:border-zinc-200"
                  onChange={() => onSupplierToggle(supplier)}
                />
                <span className="min-w-0 flex-1 truncate text-zinc-700">
                  {supplier.name}
                </span>
                {supplier.isMySupplier && (
                  <Icon icon="STAR" size={0.6} className="text-purple-500" />
                )}
              </label>
            ))}
          {!isLoading && !options.length && (
            <p className="text-xs text-zinc-400">No suppliers found</p>
          )}
        </div>
      </div>
    </FilterContainer>
  );
};

const DeliveredBySellarFilter = ({
  active,
  options,
  selectedOption,
  selectedCutoff,
  onActiveChange,
  onOptionChange,
  onCutoffChange,
}: {
  active: boolean;
  options: DeliveredBySellarOption[];
  selectedOption: DeliveredBySellarFilterOption;
  selectedCutoff: string;
  onActiveChange: (active: boolean) => void;
  onOptionChange: (
    option: Exclude<DeliveredBySellarFilterOption, "all">,
  ) => void;
  onCutoffChange: (cutoff: string) => void;
}) => (
  <section className="flex flex-col gap-3">
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-zinc-700">Delivered by Sellar</span>
      <InputSwitch
        checked={active}
        className="relative right-1 data-checked:bg-[#A34FD2] data-checked:hover:bg-[#A34FD2] data-checked:focus:ring-[#A34FD2]"
        onChange={onActiveChange}
      />
    </div>

    {active && (
      <div className="flex flex-col gap-3 pl-2">
        {options.map((option) => {
          const isSelected = selectedOption === option.value;

          return (
            <div key={option.value} className="flex flex-col gap-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  checked={isSelected}
                  className="size-4 cursor-pointer accent-[#A34FD2]"
                  name="delivered-by-sellar-window"
                  onChange={() => onOptionChange(option.value)}
                  type="radio"
                  value={option.value}
                />
                <span>{option.label}</span>
              </label>

              {isSelected && option.nestedHeader && (
                <div className="ml-2 flex flex-col gap-2 border-l border-zinc-200 pl-3">
                  <p className="text-xs font-semibold tracking-wide text-zinc-400 uppercase">
                    {option.nestedHeader}
                  </p>
                  {option.nestedOptionsLoading && (
                    <SkeletonContainer className="flex-col gap-2">
                      <SkeletonText className="h-3 w-4/5" />
                      <SkeletonText className="h-3 w-3/5" />
                    </SkeletonContainer>
                  )}
                  {!option.nestedOptionsLoading &&
                    option.nestedOptions?.map((nestedOption) => (
                      <label
                        key={nestedOption.value}
                        className="flex cursor-pointer items-center gap-2 text-[13px] text-zinc-600"
                      >
                        <input
                          checked={selectedCutoff === nestedOption.value}
                          className="size-4 cursor-pointer accent-[#A34FD2]"
                          name="delivered-by-sellar-cutoff"
                          onChange={() => onCutoffChange(nestedOption.value)}
                          type="radio"
                          value={nestedOption.value}
                        />
                        <span>{nestedOption.label}</span>
                      </label>
                    ))}
                  {!option.nestedOptionsLoading &&
                    !option.nestedOptions?.length && (
                      <p className="text-xs text-zinc-400">
                        No cut-off times available
                      </p>
                    )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </section>
);

const LoadingSearchResultsView = () => (
  <div
    aria-label="Loading products"
    aria-live="polite"
    className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-3"
    role="status"
  >
    {Array.from({ length: 8 }, (_, index) => (
      <SkeletonContainer
        key={index}
        className="flex min-w-0 flex-col gap-0 overflow-hidden rounded-xl border border-gray-200 bg-white"
      >
        <SkeletonImage className="aspect-4/3 rounded-none" />
        <div className="flex flex-col gap-3 p-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <SkeletonText className="h-4 w-3/4" />
              <SkeletonText className="h-3 w-1/2" />
            </div>
            <SkeletonText className="size-5 shrink-0 rounded-full" />
          </div>
          <SkeletonText className="h-3 w-2/5" />
          <div className="flex flex-col gap-2">
            <SkeletonText className="h-4 w-1/3" />
            <SkeletonText className="h-3 w-1/2" />
          </div>
          <SkeletonText className="mt-1 h-9 w-full rounded-md" />
        </div>
      </SkeletonContainer>
    ))}
  </div>
);

const useDiscoveryV3SearchProductListManager = () => {
  const {
    searchValue,
    styles: storedStyles,
    categoryKey,
    dietaryTypes,
    orderBy,
    containerTypes,
    selectedSuppliers,
    isMySuppliersOnly,
    abvRange,
    deliveredBySellarOption,
    isDeliveredBySellar,
    isInStockItemsOnly,
    isAvailableToPreOrder,
    orderCutOffAt,
    priceRange,
    setIsInStockItemsOnly,
    setIsAvailableToPreOrder: setStoredIsAvailableToPreOrder,
    setAbvRange,
    setStyles: setStoredStyles,
    setCategoryKey,
    setDietaryTypes,
    setContainerTypes,
    setSelectedSuppliers,
    setIsMySuppliersOnly,
    setDeliveredBySellarOption: setStoredDeliveredBySellarOption,
    setIsDeliveredBySellar,
    setOrderCutOffAt,
    setPriceRange,
    setSearchValue,
    menuActive,
    setMenuActive,
    reset,
  } = useDiscoveryV2SearchStore(
    useShallow((state) => ({
      searchValue: state.searchValue,
      styles: state.styleSearchValues,
      categoryKey: state.categoryKey,
      containerTypes: state.selectedContainerTypes,
      selectedSuppliers: state.selectedSuppliers,
      isMySuppliersOnly: state.isMySuppliersOnly,
      dietaryTypes: state.selectedDietaryTypes,
      orderBy: state.orderBy,
      abvRange: state.abvRange,
      deliveredBySellarOption: state.deliveredBySellarOption,
      isDeliveredBySellar: state.isDeliveredBySellar,
      isInStockItemsOnly: state.isInStockItemsOnly,
      isAvailableToPreOrder: state.isAvailableToPreOrder,
      orderCutOffAt: state.orderCutOffAt,
      priceRange: state.priceRange,
      setAbvRange: state.setAbvRange,
      setStyles: state.setStyleSearchValues,
      setCategoryKey: state.setCategoryKey,
      setContainerTypes: state.setContainerTypes,
      setSelectedSuppliers: state.setSelectedSuppliers,
      setIsMySuppliersOnly: state.setIsMySuppliersOnly,
      setDietaryTypes: state.setDietaryTypes,
      setDeliveredBySellarOption: state.setDeliveredBySellarOption,
      setIsDeliveredBySellar: state.setIsDeliveredBySellar,
      setIsInStockItemsOnly: state.setIsInStockItemsOnly,
      setIsAvailableToPreOrder: state.setIsAvailableToPreOrder,
      setOrderCutOffAt: state.setOrderCutOffAt,
      setPriceRange: state.setPriceRange,
      setSearchValue: state.setSearchValue,
      menuActive: state.menuActive,
      setMenuActive: state.setMenuActive,
      reset: state.reset,
    })),
  );

  const navigate = useNavigate();
  const onClickBack = () => {
    if (menuActive) {
      setMenuActive(false);
      return;
    }

    navigate(DISCOVER);
    reset();
  };

  const [supplierSearch, setSupplierSearch] = useState("");
  const [debouncedSupplierSearch] = useDebounce(supplierSearch, 300);
  const selectedSupplierId =
    selectedSuppliers.length === 1 ? selectedSuppliers[0]?.id : undefined;
  const [supplierBrowseSelection, setSupplierBrowseSelection] = useState<{
    supplierId: number | undefined;
    option: SupplierBrowseOptionBase;
  }>({ supplierId: undefined, option: { id: -1, value: "all" } });
  const selectedSupplierBrowseOption =
    supplierBrowseSelection.supplierId === selectedSupplierId
      ? supplierBrowseSelection.option
      : { id: -1, value: "all" };
  const setSelectedSupplierBrowseOption = (option: SupplierBrowseOptionBase) =>
    setSupplierBrowseSelection({ supplierId: selectedSupplierId, option });

  const styles = getSelectedDiscoveryStyles({
    styles: storedStyles,
    categoryKey,
  });

  const setStyles = (values: string[]) => {
    const selectedCategory = Object.values(filterStyles).find(
      (style) => values.length === 1 && style.searchValue === values[0],
    );

    if (selectedCategory) {
      setCategoryKey(selectedCategory.key);
      setStoredStyles([]);
      return;
    }

    setCategoryKey(undefined);
    setStoredStyles(values);
  };

  const setDeliveredBySellarActive = (active: boolean) => {
    setIsDeliveredBySellar(active);
    if (active) {
      setStoredIsAvailableToPreOrder(false);
    }
    if (!active) {
      setStoredDeliveredBySellarOption("all");
      setOrderCutOffAt(undefined);
    }
  };

  const setIsAvailableToPreOrder = (active: boolean) => {
    setStoredIsAvailableToPreOrder(active);
    if (active) {
      setDeliveredBySellarActive(false);
    }
  };

  const setDeliveredBySellarOption = (
    option: Exclude<DeliveredBySellarFilterOption, "all">,
  ) => {
    setStoredDeliveredBySellarOption(option);
    setOrderCutOffAt(undefined);
  };

  const setDeliveredBySellarCutoff = (cutoff: string) => {
    setOrderCutOffAt(cutoff);
  };

  const setMySuppliersOnly = (selected: boolean) => {
    setIsMySuppliersOnly(selected);
  };

  const toggleSupplier = (supplier: SupplierFilterOption) => {
    const isSelected = selectedSuppliers.some(({ id }) => id === supplier.id);

    if (isSelected) {
      setSelectedSupplierBrowseOption({ id: -1, value: "all" });
      setSelectedSuppliers(
        selectedSuppliers.filter(({ id }) => id !== supplier.id),
      );
      return;
    }

    setSelectedSuppliers([
      ...selectedSuppliers,
      { id: supplier.id, name: supplier.name },
    ]);
    setSelectedSupplierBrowseOption({ id: -1, value: "all" });
  };

  const clearSuppliers = () => {
    setSelectedSuppliers([]);
    setIsMySuppliersOnly(false);
    setSupplierSearch("");
    setSelectedSupplierBrowseOption({ id: -1, value: "all" });
  };

  const enableSingleSupplier = (supplier: { id: number; name: string }) => {
    setSelectedSuppliers([supplier]);
    setIsMySuppliersOnly(false);
    setSupplierSearch("");
    setSelectedSupplierBrowseOption({ id: -1, value: "all" });
  };

  const selectedSupplier =
    selectedSuppliers.length === 1 ? selectedSuppliers[0] : undefined;

  const isStorefrontTagApplied = Boolean(
    selectedSupplier && selectedSupplierBrowseOption.id > 0,
  );

  const appliedCriteria: AppliedCriterion[] = [
    ...(!isInStockItemsOnly
      ? [
          {
            kind: "regular" as const,
            id: "include-out-of-stock",
            label: "Include out of stock items",
            onRemove: () => setIsInStockItemsOnly(true),
          },
        ]
      : []),
    ...(isDeliveredBySellar
      ? [
          {
            kind: "regular" as const,
            id: "delivered-by-sellar",
            label: "Delivered by Sellar",
            onRemove: () => setDeliveredBySellarActive(false),
          },
        ]
      : []),
    ...(isDeliveredBySellar && deliveredBySellarOption !== "all"
      ? [
          {
            kind: "regular" as const,
            id: "delivered-by-sellar-window",
            label:
              deliveredBySellarAppliedFilterLabels[deliveredBySellarOption],
            onRemove: () => {
              setStoredDeliveredBySellarOption("all");
              setOrderCutOffAt(undefined);
            },
          },
        ]
      : []),
    ...(isAvailableToPreOrder
      ? [
          {
            kind: "regular" as const,
            id: "available-to-pre-order",
            label: "Available to pre-order",
            onRemove: () => setIsAvailableToPreOrder(false),
          },
        ]
      : []),
    ...(isMySuppliersOnly
      ? [
          {
            kind: "regular" as const,
            id: "my-suppliers",
            label: "My suppliers",
            onRemove: () => setIsMySuppliersOnly(false),
          },
        ]
      : []),
    ...selectedSuppliers.map((supplier) => ({
      kind: "regular" as const,
      id: `supplier-${supplier.id}`,
      label: supplier.name,
      onRemove: () =>
        setSelectedSuppliers(
          selectedSuppliers.filter(({ id }) => id !== supplier.id),
        ),
    })),
    ...styles.map((id) => ({
      kind: "regular" as const,
      id: `style-${id}`,
      label:
        localMarketSuggestions.find(
          (selection) => selection.kind === "style" && selection.value === id,
        )?.label ?? getOptionLabel(styleOptions, id),
      onRemove: () => setStyles(removeOption(styles, id)),
    })),
    ...containerTypes.map((id) => ({
      kind: "regular" as const,
      id: `container-${id}`,
      label: getOptionLabel(containerOptions, id),
      onRemove: () =>
        setContainerTypes(removeOption(containerTypes, id, containerOptions)),
    })),
    ...(priceRange
      ? [
          {
            kind: "regular" as const,
            id: "price",
            label: getRangeLabel(priceRange, formatCurrency),
            onRemove: () => setPriceRange(undefined),
          },
        ]
      : []),
    ...(abvRange
      ? [
          {
            kind: "regular" as const,
            id: "abv",
            label: getRangeLabel(abvRange, (value) => `${value}%`),
            onRemove: () => setAbvRange(undefined),
          },
        ]
      : []),
    ...dietaryTypes.map((id) => ({
      kind: "regular" as const,
      id: `dietary-${id}`,
      label: getOptionLabel(dietaryOptions, id),
      onRemove: () => setDietaryTypes(removeOption(dietaryTypes, id)),
    })),
  ];

  const supplierStorefrontTagsListQuery = useStorefrontTagList(
    {
      query: selectedSupplier
        ? [
            {
              type: "SupplierId",
              value: selectedSupplier.id,
              modifier: "EQUALS",
            } as const,
          ]
        : [],
    },
    { enabled: !!selectedSupplier },
  );

  const supplierStorefrontTags =
    supplierStorefrontTagsListQuery.data?.pages.flat() ?? [];

  const { business } = useSellarCurrentBusiness();
  const [unAuthorisedUserPostcode] = useLocalStorage<string | undefined>(
    "unauthorisedUserPostcode",
    undefined,
  );
  const buyerPostcode =
    business?.type === "retailer"
      ? business.DeliveryAddress.postalCode
      : (unAuthorisedUserPostcode ?? "");

  const supplierListQuery = useDiscoverySupplierList(
    {
      limit: 10,
      search: debouncedSupplierSearch,
      unAuthorisedUserPostcode,
      retailerPostcode: buyerPostcode,
      query: [
        ...(isDeliveredBySellar
          ? ([
              {
                type: "deliveredBySellar",
                modifier: "EQUALS",
                value: true,
              },
            ] as const)
          : []),
        ...(isMySuppliersOnly
          ? ([
              {
                type: "mySuppliersOnly",
                modifier: "EQUALS",
                value: true,
              },
            ] as const)
          : []),
      ],
    },
    { placeholderData: keepPreviousData },
  );

  const buyerRelationshipCountQuery = useBuyerRelationshipCount({
    query: [
      {
        type: "name",
        modifier: "INCLUDES",
        value: debouncedSupplierSearch,
      },
    ],
  });

  const supplierOptions: SupplierFilterOption[] =
    supplierListQuery.data?.pages.flat().map((supplier) => ({
      id: supplier.id,
      name: supplier.Business.name,
      isMySupplier: Boolean(supplier.SupplierRetailerConnection),
    })) ?? [];

  const deliveryWindowRanges = getDeliveryWindowRanges();

  const selectedDeliveryWindow =
    isDeliveredBySellar && deliveredBySellarOption !== "all"
      ? deliveryWindowRanges[deliveredBySellarOption]
      : undefined;

  const { upcomingDeliveryGroups, isLoading: cutoffOptionsLoading } =
    useGetUpcomingDeliveryGroups({
      buyerPostcode,
      enabled: isDeliveredBySellar && Boolean(buyerPostcode),
      arrivingByDate: {
        low: deliveryWindowRanges.weekend.low,
        high: deliveryWindowRanges.weekend.high,
      },
    });

  const cutoffOptions = upcomingDeliveryGroups
    .filter((group) =>
      group.deliveryDates.some((deliveryDate) => {
        const date = dayjs(deliveryDate);
        return (
          !date.isBefore(deliveryWindowRanges.weekend.low) &&
          !date.isAfter(deliveryWindowRanges.weekend.high)
        );
      }),
    )
    .toSorted((first, second) =>
      dayjs(first.orderCutOffDateTime).diff(second.orderCutOffDateTime),
    )
    .map((group) => ({
      value: dayjs(group.orderCutOffDateTime).toISOString(),
      label: `Order by ${dayjs(group.orderCutOffDateTime).format("h:mma dddd")}`,
    }));

  const deliveredBySellarOptions: DeliveredBySellarOption[] = [
    {
      value: "weekend",
      label: "Get by the weekend",
      nestedHeader: "Shop by cut-off time",
      nestedOptions: cutoffOptions,
      nestedOptionsLoading: cutoffOptionsLoading,
    },
    { value: "next-week", label: "Next week" },
    { value: "week-after-next", label: "Week after next" },
  ];

  const styleSearchString = storedStyles.length
    ? `(${storedStyles.join("|")})`
    : "";
  const categorySearchString = categoryKey
    ? `(${filterStyles[categoryKey].searchValue})`
    : "";

  const discoveryListQuery = useDiscoveryV2ProductList({
    limit: 10,
    sortBy: orderBy,
    search: [searchValue, categorySearchString, styleSearchString]
      .filter(Boolean)
      .join(" & "),
    unAuthorisedUserPostcode,
    retailerPostcode: buyerPostcode,
    query: [
      ...(isInStockItemsOnly
        ? ([
            {
              type: "inStockItemsOnly",
              modifier: "EQUALS",
              value: true,
            },
          ] as const)
        : []),
      ...(selectedSuppliers.length
        ? ([
            {
              type: "supplierId",
              modifier: "IN",
              value: selectedSuppliers.map(({ id }) => id),
            },
          ] as const)
        : []),
      ...(isStorefrontTagApplied
        ? [
            {
              type: "storefrontTagId" as const,
              modifier: "IN" as const,
              value: [selectedSupplierBrowseOption.id],
            },
          ]
        : []),
      ...(isMySuppliersOnly
        ? ([
            {
              type: "mySuppliersOnly",
              modifier: "EQUALS",
              value: true,
            },
          ] as const)
        : []),
      ...(containerTypes.length
        ? ([
            {
              type: "containerType",
              modifier: "IN",
              value: Array.from(containerTypes.values()),
            },
          ] as const)
        : []),
      ...(dietaryTypes.length
        ? ([
            {
              type: "dietaryType",
              modifier: "IN",
              value: Array.from(dietaryTypes.values()),
            },
          ] as const)
        : []),
      ...buildRangeQuery("abv", abvRange),
      ...buildRangeQuery("price", priceRange),
      ...(isDeliveredBySellar
        ? ([
            {
              type: "deliveredBySellar",
              modifier: "EQUALS",
              value: true,
            },
          ] as const)
        : []),
      ...(isAvailableToPreOrder
        ? ([
            {
              type: "isAvailableToPreorder",
              modifier: "EQUALS",
              value: true,
            },
          ] as const)
        : []),
      ...(selectedDeliveryWindow
        ? ([
            {
              type: "arrivingByDate",
              modifier: "BETWEEN",
              value: selectedDeliveryWindow,
            },
          ] as const)
        : []),
      ...(isDeliveredBySellar &&
      deliveredBySellarOption === "weekend" &&
      orderCutOffAt
        ? ([
            {
              type: "orderCutOffAt",
              modifier: "BETWEEN",
              value: {
                low: dayjs(orderCutOffAt).toDate(),
                high: dayjs(orderCutOffAt).toDate(),
              },
            },
          ] as const)
        : []),
    ],
  });

  const supplierBrowseOptions: SupplierBrowseOption[] = [
    { value: "all", label: "All", id: -1 },
    ...supplierStorefrontTags.map((tag) => ({
      id: tag.id,
      value: tag.value,
      label: tag.value,
    })),
  ];

  const products = discoveryListQuery.data?.pages.flat() ?? [];

  const singleSupplierCard = useSingleSupplierCardManager({
    selectedSupplier,
    browseOptions: supplierBrowseOptions,
    selectedBrowseOption: selectedSupplierBrowseOption,
  });

  const trackingMethods = useDiscoveryTrackingManager({
    isDeliveredBySellar,
    selectedDeliveredBySellarOption: deliveredBySellarOption,
    selectedSupplierCount: selectedSuppliers.length,
    selectedCutOffAt: orderCutOffAt ? dayjs(orderCutOffAt).toDate() : undefined,
    productListQueryLoading: discoveryListQuery.isLoading,
    productListResultCount: products.length,
  });

  const searchTerm = searchValue.trim();
  if (searchTerm) {
    appliedCriteria.unshift({
      kind: "search",
      id: "search",
      label: searchTerm,
      onRemove: () => {
        setSearchValue("");
        trackingMethods.trackSearchSubmitted("");
      },
    });
  }

  if (isStorefrontTagApplied) {
    appliedCriteria.push({
      kind: "storefront-tag",
      id: `storefront-tag-${selectedSupplierBrowseOption.id}`,
      label: selectedSupplierBrowseOption.value,
      onRemove: () => setSelectedSupplierBrowseOption({ id: -1, value: "all" }),
    });
  }

  const regularFilters = appliedCriteria.filter(
    (criterion) => criterion.kind === "regular",
  );

  return {
    rawQuery: discoveryListQuery,
    productList: products,
    appliedCriteria,
    regularFilters,
    styles,
    dietaryTypes,
    deliveredBySellarFilter: {
      active: isDeliveredBySellar,
      options: deliveredBySellarOptions,
      selectedCutoff: orderCutOffAt,
      selectedOption: deliveredBySellarOption,
    },
    suppliersFilter: {
      options: supplierOptions,
      selectedSupplierIds: selectedSuppliers.map(({ id }) => id),
      searchValue: supplierSearch,
      isMySuppliersOnly,
      mySuppliersCount: buyerRelationshipCountQuery.data ?? 0,
      isLoading: supplierListQuery.isPending,
    },
    singleSupplierCard,
    containerTypes,
    priceRange,
    abvRange,
    isInStockItemsOnly,
    isAvailableToPreOrder,
    searchValue,
    setIsInStockItemsOnly,
    setIsAvailableToPreOrder,
    setAbvRange,
    setPriceRange,
    setStyles,
    setDietaryTypes,
    setDeliveredBySellarActive,
    setDeliveredBySellarCutoff,
    setDeliveredBySellarOption,
    setMySuppliersOnly,
    setSupplierSearch,
    toggleSupplier,
    clearSuppliers,
    enableSingleSupplier,
    removeSingleSupplier: clearSuppliers,
    setSelectedSupplierBrowseOption,
    setContainerTypes,
    setSearchValue,
    onClickBack,
    ...trackingMethods,
  };
};

const useSingleSupplierCardManager = ({
  selectedSupplier,
  browseOptions,
  selectedBrowseOption,
}: {
  selectedSupplier: { id: number; name: string } | undefined;
  browseOptions: SupplierBrowseOption[];
  selectedBrowseOption: SupplierBrowseOptionBase;
}): SingleSupplierCardData | undefined => {
  const supplierId = selectedSupplier?.id ?? 0;

  const supplierQuery = useSupplier(supplierId, {
    enabled: !!selectedSupplier,
  });

  const storefrontQuery = useSupplierStorefrontV2(
    supplierId,
    {},
    { enabled: !!selectedSupplier },
  );

  const basketsQuery = useUnifiedCheckoutBasketList();

  const supplierBaskets =
    basketsQuery.data?.filter((basket) =>
      basket.DraftOrders.some((draft) => draft.SupplierId === supplierId),
    ) ?? [];

  const { orders } = useUnifiedCheckoutDraftOrders(supplierBaskets);

  const storefront = storefrontQuery.data;
  const orderItems = orders.flatMap(
    (order) =>
      order.OrderItems?.filter((item) => !isPromotionalOrderItem(item)) ?? [],
  );

  const activeVolumeDiscountIds = Array.from(
    new Set(
      orders.flatMap((order) =>
        calculateUnifiedCheckoutOrderPricing({
          orderItems:
            order.OrderItems?.filter((item) => !isPromotionalOrderItem(item)) ??
            [],
          storefront,
          vatPercentage: 0,
        }).appliedVolumeDiscounts.map(
          ({ volumeDiscount }) => volumeDiscount.id,
        ),
      ),
    ),
  );

  if (!selectedSupplier) return undefined;

  return {
    id: selectedSupplier.id,
    name: supplierQuery.data?.Business.name ?? selectedSupplier.name,
    logoUrl: supplierQuery.data?.Business.logoUrl ?? "",
    description: supplierQuery.data?.description ?? "",
    browseOptions,
    selectedBrowseOption,
    volumeDiscounts: storefront?.VolumeDiscounts?.PriceTierDiscounts ?? [],
    activeVolumeDiscountIds,
    orderItems,
    currency: supplierQuery.data?.currency ?? "gbp",
  };
};

type SingleSupplierCardProps = SingleSupplierCardData & {
  onBrowseOptionChange: (value: SupplierBrowseOption) => void;
  onRemove: () => void;
};

type SingleSupplierCardLayoutProps = Omit<
  SingleSupplierCardProps,
  "id" | "orderItems" | "currency"
> & {
  onShowDiscounts: () => void;
};

const SingleSupplierCard = (props: SingleSupplierCardProps) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const CardLayout = isDesktop
    ? SingleSupplierCardDesktop
    : SingleSupplierCardMobile;

  return (
    <>
      <CardLayout
        {...props}
        onShowDiscounts={() => setDiscountDialogOpen(true)}
      />
      <SupplierVolumeDiscountDialog
        active={discountDialogOpen}
        activeVolumeDiscountIds={props.activeVolumeDiscountIds}
        currency={props.currency}
        discounts={props.volumeDiscounts}
        orderItems={props.orderItems}
        onDismiss={() => setDiscountDialogOpen(false)}
        supplierName={props.name}
      />
    </>
  );
};

const SupplierDescription = ({ description }: { description: string }) => {
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const descriptionId = useId();
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  useLayoutEffect(() => {
    const element = descriptionRef.current;
    if (!element) {
      return;
    }
    const measureOverflow = () => {
      const lineHeight = Number.parseFloat(
        getComputedStyle(element).lineHeight,
      );
      setHasOverflow(element.scrollHeight > lineHeight * 2 + 1);
    };
    measureOverflow();
    const observer = new ResizeObserver(measureOverflow);
    observer.observe(element);
    return () => observer.disconnect();
  }, [description]);

  const isExpanded = expanded && hasOverflow;

  return (
    <div className="text-sm leading-5 text-zinc-500">
      <p
        id={descriptionId}
        ref={descriptionRef}
        className={twMerge(!isExpanded && "line-clamp-2")}
      >
        {description}
      </p>
      {hasOverflow && (
        <button
          aria-controls={descriptionId}
          aria-expanded={isExpanded}
          className="mt-1 cursor-pointer font-medium text-[#A34FD2]"
          onClick={() => setExpanded((current) => !current)}
          type="button"
        >
          Show {isExpanded ? "less" : "more"}
        </button>
      )}
    </div>
  );
};

const SingleSupplierCardDesktop = ({
  name,
  logoUrl,
  description,
  browseOptions,
  selectedBrowseOption,
  volumeDiscounts,
  activeVolumeDiscountIds,
  onBrowseOptionChange,
  onRemove,
  onShowDiscounts,
}: SingleSupplierCardLayoutProps) => {
  return (
    <article className="flex flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-md">
      <div className="relative flex gap-5 p-5">
        <CardImage
          className="h-12 w-12 shrink-0 rounded-full border border-zinc-200"
          fallback="supplier-badge"
          imageUrl={logoUrl}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <h2 className="text-lg font-semibold text-zinc-800">{name}</h2>
          {description && (
            <SupplierDescription key={description} description={description} />
          )}
        </div>
        <button
          aria-label={`Remove ${name} filter`}
          className="absolute top-5 right-5 flex cursor-pointer items-center gap-1 text-sm font-semibold text-zinc-600"
          onClick={onRemove}
          type="button"
        >
          <Icon icon="CLOSE" size={0.8} className="relative top-[0.4px]" />
          <span className="hidden md:inline">Remove filter</span>
        </button>
      </div>

      <div className="flex flex-col gap-4 border-t border-zinc-200 px-5 py-4">
        {browseOptions.length > 1 && (
          <div className="flex items-center gap-4">
            <p className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">
              Browse
            </p>
            <div className="flex flex-wrap gap-2">
              {browseOptions.map((option) => {
                const selected = option.value === selectedBrowseOption.value;
                return (
                  <Button
                    color="slim"
                    size="small"
                    key={option.value}
                    onClick={() => onBrowseOptionChange(option)}
                    selected={selected}
                    aria-pressed={selected}
                    className="shadow-sm"
                  >
                    {option.label}
                  </Button>
                );
              })}
            </div>
          </div>
        )}

        {!!volumeDiscounts.length && (
          <div className="flex">
            <button
              className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border border-purple-300 bg-purple-100 px-4 py-3 text-left text-sm font-semibold text-purple-900 hover:bg-purple-200"
              onClick={onShowDiscounts}
              type="button"
            >
              <span className="flex min-w-0 items-center gap-3">
                <Icon
                  className="shrink-0"
                  icon="TAG_MULTIPLE_OUTLINE"
                  size={0.68}
                />
                <span className="text-zinc-700">
                  {volumeDiscounts.length} volume{" "}
                  {pluralize("discount", volumeDiscounts.length)} available
                </span>
              </span>
              <span className="flex shrink-0 items-center gap-3">
                {!!activeVolumeDiscountIds.length && (
                  <span className="rounded-full bg-purple-600 px-3 py-1 text-xs text-white">
                    {activeVolumeDiscountIds.length} active
                  </span>
                )}
                <Icon icon="CHEVRON_RIGHT" size={0.8} />
              </span>
            </button>
          </div>
        )}
      </div>
    </article>
  );
};

const SingleSupplierCardMobile = ({
  name,
  logoUrl,
  description,
  browseOptions,
  selectedBrowseOption,
  volumeDiscounts,
  onBrowseOptionChange,
  onRemove,
  onShowDiscounts,
}: SingleSupplierCardLayoutProps) => {
  return (
    <article className="flex flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-md">
      <div className="flex flex-col gap-2 p-4">
        <div className="flex items-center gap-2">
          <CardImage
            className="h-6 w-6 shrink-0 rounded-full border border-zinc-200"
            fallback="supplier-badge"
            imageUrl={logoUrl}
          />
          <h2 className="min-w-0 flex-1 text-base font-semibold text-zinc-800">
            {name}
          </h2>
          <button
            aria-label={`Remove ${name} filter`}
            className="flex shrink-0 cursor-pointer items-center justify-center p-1 text-zinc-600"
            onClick={onRemove}
            type="button"
          >
            <Icon icon="CLOSE" size={0.8} />
          </button>
        </div>
        {description && (
          <SupplierDescription key={description} description={description} />
        )}
      </div>
      <div className="flex flex-col gap-2 border-t border-zinc-200 p-4">
        <InputContainer className="gap-1 px-3 py-2 text-sm shadow-sm">
          <span className="shrink-0">Browse:</span>
          <InputSelect
            aria-label={`Browse ${name}'s range`}
            className="min-w-0 font-semibold"
            value={selectedBrowseOption.value}
            onChange={(event) => {
              const option = browseOptions.find(
                (option) => option.value === event.target.value,
              );
              if (option) {
                onBrowseOptionChange(option);
              }
            }}
          >
            {browseOptions.map((option) => (
              <InputOption key={option.value} value={option.value}>
                {option.label}
              </InputOption>
            ))}
          </InputSelect>
        </InputContainer>
        {!!volumeDiscounts.length && (
          <button
            className="flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border border-purple-300 bg-purple-100 px-3 py-2 text-left text-sm font-semibold text-purple-900"
            onClick={onShowDiscounts}
            type="button"
          >
            <span className="flex min-w-0 items-center gap-1">
              <Icon className="shrink-0" icon="TAG_MULTIPLE_OUTLINE" size={1} />
              <span className="text-zinc-700">
                {volumeDiscounts.length} volume discounts available
              </span>
            </span>
            <Icon className="shrink-0" icon="CHEVRON_RIGHT" size={0.8} />
          </button>
        )}
      </div>
    </article>
  );
};

const SupplierVolumeDiscountDialog = ({
  active,
  activeVolumeDiscountIds,
  currency,
  discounts,
  orderItems,
  supplierName,
  onDismiss,
}: {
  active: boolean;
  activeVolumeDiscountIds: number[];
  currency: string;
  discounts: SupplierVolumeDiscount[];
  orderItems: BasketOrderItem[];
  supplierName: string;
  onDismiss: () => void;
}) => {
  const groups = Object.entries(
    discounts.reduce<Record<string, SupplierVolumeDiscount[]>>(
      (groupedDiscounts, discount) => {
        const groupName = getItemTypeTitle(discount);
        groupedDiscounts[groupName] = [
          ...(groupedDiscounts[groupName] ?? []),
          discount,
        ];
        return groupedDiscounts;
      },
      {},
    ),
  );

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        onClickLeftIcon={onDismiss}
        title={`${supplierName}'s volume discounts`}
        description={`Check your progress towards qualifying for ${supplierName}'s volume discounts`}
        leftIcon="CHEVRON_DOWN"
      />
      <DialogContent>
        <div className="flex flex-col gap-4">
          {groups.map(([groupName, groupDiscounts]) => (
            <section className="flex flex-col gap-2" key={groupName}>
              <h3 className="text-xs font-semibold tracking-wide text-zinc-500 uppercase">
                {groupName}
              </h3>
              <div className="flex flex-col gap-2">
                {groupDiscounts.map((discount) => {
                  const description = getDiscountDescription({
                    discount,
                    currency,
                  });

                  const orderItemsForDiscount = orderItems.filter((orderItem) =>
                    getContainerTypes(orderItem.Product.containerType).includes(
                      discount.itemType ?? "",
                    ),
                  );
                  const quantityOfOrderItems = orderItemsForDiscount.reduce(
                    (total, orderItem) => total + orderItem.quantity,
                    0,
                  );

                  const orderItemsForDiscountPrice = orderItems.reduce(
                    (total, orderItem) => total + orderItem.price,
                    0,
                  );

                  const discountByType =
                    discount.minimumBasketValue > 0
                      ? "minimumBasketValue"
                      : "minimumOrderQuantity";

                  const thresholdMet =
                    discountByType === "minimumOrderQuantity"
                      ? quantityOfOrderItems >= discount.quantity
                      : orderItemsForDiscountPrice >=
                        discount.minimumBasketValue;

                  const percentage =
                    (quantityOfOrderItems / discount.quantity) * 100;

                  const active = activeVolumeDiscountIds.includes(discount.id);

                  return (
                    <div
                      className={twMerge(
                        "flex flex-col gap-4 rounded-lg border px-3 py-2 font-semibold",
                        active && "border-purple-300 bg-purple-100",
                        !active && "border-zinc-200",
                      )}
                      key={discount.id}
                    >
                      <div className="flex items-center justify-between">
                        <div
                          className={twMerge(
                            "text-sm",
                            active && "text-purple-800",
                            !active && "text-zinc-800",
                          )}
                        >
                          {description}
                        </div>
                        {thresholdMet ? (
                          <Icon
                            className="shrink-0 text-purple-800"
                            icon="CHECKBOX_MARKED_CIRCLE"
                            size={0.9}
                          />
                        ) : (
                          <Icon
                            className="shrink-0"
                            icon="CHEVRON_RIGHT"
                            size={0.9}
                          />
                        )}
                      </div>
                      {Boolean(percentage) && !active && (
                        <ProgressBarPurple
                          className={twMerge(
                            "h-2 bg-purple-200",
                            thresholdMet && !active && "opacity-40",
                          )}
                          percentage={percentage}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const buildDiscoveryV3SearchProduct = ({
  product,
  deliveredBySellarFilterApplied,
  canRequestPricing,
}: {
  product: DiscoveryV2ListResponseType[number];
  deliveredBySellarFilterApplied: boolean;
  canRequestPricing: boolean;
}): Omit<
  ProductSearchCardProps,
  | "onAddToBasket"
  | "onIncrementQuantityInBasket"
  | "onDecrementQuantityInBasket"
  | "onViewProduct"
  | "basketQuantity"
  | "canAddToBasket"
  | "basketActionDisabled"
> => {
  const isSellarDistributionOrder = Boolean(
    getDiscoveryProductDeliveryEstimate(product),
  );
  const [productNamePart, productFormatPart] = product.name?.split(" • ") ?? [];
  const productName = productNamePart ?? "";
  const supplierName = product.Supplier.Business.name;
  const abv = product.Parent?.abv ?? undefined;
  const productPrice = getDiscoveryProductPrice(
    { price: product.price, basePrice: null, fullPrice: null },
    canRequestPricing,
  );
  const imageUrls = [product.Parent?.heroImageUrl || ""].filter(Boolean);
  const badgeImageUrl = product.Parent?.imageUrl ?? undefined;
  const formatLabel = productFormatPart || "";
  const style = product.Parent?.kind ?? undefined;
  const overlay = getDiscoveryProductOverlay({
    product,
    deliveredBySellarFilterApplied,
    deliveryEstimate: getDiscoveryProductDeliveryEstimate(product),
  });
  const supplierLogoUrl = product.Supplier.Business.logoUrl ?? "";
  const description = product.Parent?.description || "";
  const supplierUrlId = product.Supplier.urlId ?? "";
  const dietaryTags: ProductDietaryTag[] = [
    product.Parent?.vegan && "vegan",
    product.Parent?.lactoseFree && "lactose-free",
    product.Parent?.glutenFree && "gluten-free",
  ].filter(Boolean);
  const variant =
    isSellarDistributionOrder && deliveredBySellarFilterApplied
      ? "sd"
      : "non-sd";
  const parentProductId = product.Parent?.id;

  return {
    productItemId: product.id,
    name: productName,
    supplierName,
    supplierUrlId,
    supplierLogoUrl,
    availableStock: product.availableStock,
    canBackorder: product.canBackorder,
    description,
    style,
    abv,
    price: productPrice,
    imageUrls,
    badgeImageUrl,
    dietaryTags,
    variant,
    formatLabel,
    overlay,
    parentProductId,
    deliveryEstimate: getDiscoveryProductDeliveryEstimate(product),
  };
};

const DiscoveryProductDialog = (
  props: Omit<UseDiscoveryProductDialogOptions, "selectedProduct"> & {
    active: boolean;
    selectedProduct: DiscoveryProductSelection | undefined;
    afterLeave: () => void;
  },
) => {
  const { selectedProduct } = props;

  return (
    <Dialog
      active={props.active}
      fullHeight
      className="w-full max-w-150 rounded-none md:h-[85dvh] md:rounded-2xl"
      onDismiss={props.onDismiss}
      afterLeave={props.afterLeave}
    >
      {selectedProduct && (
        <DiscoveryProductDialogBody
          {...props}
          key={`${selectedProduct.parentProductId}:${selectedProduct.productItemId}`}
          selectedProduct={selectedProduct}
        />
      )}
    </Dialog>
  );
};

const DiscoveryProductDialogBody = (
  props: UseDiscoveryProductDialogOptions,
) => {
  const {
    state,
    title,
    basketActions,
    orderRequirements,
    reloadProduct,
    viewSupplierProducts,
    visitSupplierStorefront,
  } = useDiscoveryProductDialog(props);

  return (
    <>
      {state.status === "ready" && (
        <DiscoveryProductDialogContent
          key={state.product.productItemId}
          product={state.product}
          requirements={orderRequirements}
          basketActions={basketActions}
          onDismiss={props.onDismiss}
          onShowSupplier={viewSupplierProducts}
          onShopWithSupplier={visitSupplierStorefront}
        />
      )}
      {state.status !== "ready" && (
        <div className="flex shrink-0 items-center gap-3 p-4">
          <ProductDialogCloseButton onDismiss={props.onDismiss} />
          <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
        </div>
      )}
      {state.status === "loading" && (
        <DialogContent className="flex min-h-40 items-center justify-center p-6">
          <div role="status" aria-label="Loading product details">
            <Spinner size="lg" />
          </div>
        </DialogContent>
      )}
      {state.status === "error" && (
        <DialogContent className="p-4">
          <AlertContainer
            role="alert"
            type="error"
            className="flex w-full flex-col justify-between gap-3 px-3 py-3 text-sm text-red-800 sm:flex-row sm:items-center"
          >
            <div className="flex items-center gap-2">
              <Icon icon="ALERT" className="shrink-0" />
              <p>
                <span className="font-semibold">
                  Unable to load this product
                </span>{" "}
                &ndash; Please try again.
              </p>
            </div>
            <button
              type="button"
              onClick={reloadProduct}
              className="shrink-0 cursor-pointer font-semibold text-nowrap underline underline-offset-2"
            >
              Reload product
            </button>
          </AlertContainer>
        </DialogContent>
      )}
    </>
  );
};

const DiscoveryProductDialogContent = ({
  product,
  requirements,
  basketActions,
  onDismiss,
  onShowSupplier,
  onShopWithSupplier,
}: {
  product: DiscoveryProductDetails;
  requirements: OrderRequirement[];
  basketActions: DiscoveryProductBasketActions;
  onDismiss: () => void;
  onShowSupplier: () => void;
  onShopWithSupplier: () => void;
}) => {
  const metadata = [
    product.style,
    product.abv !== undefined && formatAbv(product.abv),
    ...product.dietaryTags.map((tag) => dietaryTagLabels[tag].label),
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <>
      <ProductDialogHero
        product={product}
        onDismiss={onDismiss}
        onShowSupplier={onShowSupplier}
      />
      <DialogContent className="flex flex-col gap-4 p-4 pt-3 md:p-6 md:pt-4">
        {metadata && (
          <p className="text-base font-medium text-zinc-800">{metadata}</p>
        )}
        {product.description && (
          <ProductDialogDescription description={product.description} />
        )}
        <DiscoveryProductSkuDetails product={product} />
        <SkuProfitCalculator sku={product.sku} />
        {product.variant === "non-sd" && (
          <ProductDialogSupplierInformation
            otherFormats={product.otherFormats}
            requirements={requirements}
          />
        )}
      </DialogContent>
      <DialogCTA className="items-center gap-4 space-x-0 px-4 md:px-6">
        <ProductDialogFooter
          product={product}
          basketActions={basketActions}
          onShowSupplier={onShowSupplier}
          onShopWithSupplier={onShopWithSupplier}
        />
      </DialogCTA>
    </>
  );
};

const ProductDialogCloseButton = ({ onDismiss }: { onDismiss: () => void }) => (
  <button
    aria-label="Close product details"
    className="flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/80 text-zinc-700 shadow-sm hover:bg-white"
    onClick={onDismiss}
    type="button"
  >
    <Icon icon="CHEVRON_DOWN" size={0.8} />
  </button>
);

const ProductDialogHero = ({
  product,
  onDismiss,
  onShowSupplier,
}: {
  product: DiscoveryProductDetails;
  onDismiss: () => void;
  onShowSupplier: () => void;
}) => {
  const images = Array.from(
    new Set([product.heroImageUrl, ...product.imageUrls].filter(Boolean)),
  );
  const showImageNavigation = useMediaQuery("(min-width: 768px)");

  return (
    <div className="relative aspect-video shrink-0 bg-white">
      <div className="absolute inset-0 overflow-hidden">
        {images.length ? (
          <ImageSlider
            images={images}
            showDots={showImageNavigation}
            includeThumbnails={showImageNavigation}
            imageClassName="rounded-none"
            dotsClassName="bottom-0 left-auto right-4 z-10 w-24 md:bottom-12 md:right-6 md:w-44"
            thumbnailsClassName="absolute right-6 bottom-0 z-10 hidden w-auto max-w-44 overflow-x-auto pt-0 md:flex"
            thumbnailClassName="size-10 overflow-hidden rounded-md border border-transparent"
            activeThumbnailClassName="border-purple-600"
          />
        ) : (
          <CardImage
            className="h-full w-full rounded-none"
            fallback="product-hero"
            imageUrl=""
          />
        )}
      </div>
      <div className="pointer-events-none absolute inset-x-0 top-1/2 -bottom-px bg-[linear-gradient(to_top,#fff_20%,rgba(255,255,255,0)_100%)]" />
      <div className="absolute top-3 left-3">
        <ProductDialogCloseButton onDismiss={onDismiss} />
      </div>
      <ProductDialogHeader
        product={product}
        onShowSupplier={onShowSupplier}
        className={twMerge(
          "absolute inset-x-4 bottom-0 md:inset-x-6",
          images.length > 1 && "md:pr-48",
        )}
      />
    </div>
  );
};

const ProductDialogHeader = ({
  product,
  className,
  onShowSupplier,
}: {
  product: Pick<
    DiscoveryProductDetails,
    "name" | "supplierName" | "badgeImageUrl"
  >;
  className?: string;
  onShowSupplier: () => void;
}) => (
  <div className={twMerge("flex min-w-0 items-center gap-2", className)}>
    <CardImage
      className="size-10 shrink-0 rounded-full border border-zinc-200 md:size-12"
      fallback="product-badge"
      imageUrl={product.badgeImageUrl ?? ""}
    />
    <div className="min-w-0">
      <DialogTitle
        className="truncate text-lg font-semibold text-zinc-800"
        title={product.name}
      >
        {product.name}
      </DialogTitle>
      <button
        type="button"
        onClick={onShowSupplier}
        className="flex max-w-full cursor-pointer items-center gap-0.5 text-left text-sm text-purple-700"
      >
        <span className="truncate text-base font-medium">
          {product.supplierName}
        </span>
        <Icon
          icon="CHEVRON_RIGHT"
          size={0.65}
          className="relative top-px shrink-0"
        />
      </button>
    </div>
  </div>
);

const ProductDialogDescription = ({ description }: { description: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const descriptionId = useId();
  useLayoutEffect(() => {
    const element = descriptionRef.current;
    if (!element) {
      return;
    }
    const measure = () => {
      const lineHeight = Number.parseFloat(
        window.getComputedStyle(element).lineHeight,
      );
      setHasOverflow(element.scrollHeight > lineHeight * 2 + 1);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [description]);

  return (
    <div className="font-regular relative text-base leading-5 text-zinc-600">
      <p
        ref={descriptionRef}
        id={descriptionId}
        className={twMerge(
          "whitespace-pre-line",
          !expanded && "line-clamp-2",
          !expanded && hasOverflow && "pr-12",
        )}
      >
        {description}
        {expanded && (
          <>
            {" "}
            <button
              type="button"
              className="cursor-pointer text-xs font-semibold text-purple-700"
              aria-expanded={expanded}
              aria-controls={descriptionId}
              onClick={() => setExpanded(false)}
            >
              LESS
            </button>
          </>
        )}
      </p>
      {!expanded && hasOverflow && (
        <button
          type="button"
          className="absolute right-0 bottom-0 cursor-pointer bg-white pl-1 text-sm font-semibold text-purple-700"
          aria-expanded={expanded}
          aria-controls={descriptionId}
          onClick={() => setExpanded(true)}
        >
          MORE
        </button>
      )}
    </div>
  );
};

const getProductDialogContainerIcon = (
  containerType: string | undefined,
): IconType => {
  const types = getContainerTypes(containerType);
  if (types.includes("can")) {
    return "CAN";
  }
  if (types.includes("bottle")) {
    return "BOTTLE";
  }
  if (types.includes("cask")) {
    return "CASK";
  }
  if (types.includes("keg")) {
    return "KEG";
  }
  return "DELIVERY_BOX";
};

const DiscoveryProductSkuDetails = ({
  product,
}: {
  product: DiscoveryProductDetails;
}) => {
  const stock = getStockDisplay({
    initStock: product.availableStock,
    canBackorder: product.canBackorder,
    type: "full",
    showStock: false,
  });

  const price = product.sku.price;

  const isPriceAvailable =
    price.type === "fixed" || price.type === "starting-at";

  const originalPrice = isPriceAvailable
    ? (price.basePrice ?? price.fullPrice)
    : null;

  const showDiscount =
    isPriceAvailable &&
    ((price.fullPrice !== null && price.fullPrice !== price.price) ||
      (price.basePrice ?? 0) > price.price);

  const estimate = product.deliveryEstimate;

  const canShowDeliveryEstimate =
    product.variant === "sd" &&
    estimate &&
    dayjs(estimate.orderCutoffDate).isAfter(dayjs());

  return (
    <div className="flex flex-col gap-3 pt-1 md:flex-row md:items-start md:justify-between md:gap-4">
      <div className="flex min-w-0 items-center gap-3">
        <div className="hidden size-12 shrink-0 items-center justify-center rounded-md bg-zinc-100 md:flex">
          <Icon
            icon={getProductDialogContainerIcon(product.sku.containerType)}
            size={1}
          />
        </div>
        <div className="min-w-0">
          <p className="text-base font-semibold text-zinc-800">
            {product.sku.formatLabel}
          </p>
          <p className="text-xs leading-5 text-zinc-600">
            <span className={twMerge(stock.textColor, "text-sm font-medium")}>
              {stock.stockQuantity}
            </span>
            {canShowDeliveryEstimate && (
              <span className="text-sm font-medium">
                • Get it by {dayjs(estimate.deliveryDate).format("dddd Do")}
              </span>
            )}
          </p>
          {product.overlay?.type === "available-from" && (
            <p className="text-xs text-purple-700">
              Available from {product.overlay.label}
            </p>
          )}
        </div>
      </div>
      <div className="shrink-0 text-sm text-zinc-800 md:text-right">
        {showDiscount && originalPrice !== null && (
          <span className="mr-1 text-xs text-zinc-500 line-through">
            {formatCurrency(originalPrice)}
          </span>
        )}
        <span className="text-base font-semibold">
          <ProductPricing price={price} />
        </span>
        {isPriceAvailable && (
          <span className="ml-1 text-zinc-600 md:ml-0 md:block">(ex. VAT)</span>
        )}
      </div>
    </div>
  );
};

const ProductDialogSupplierInformation = ({
  otherFormats,
  requirements,
}: {
  otherFormats: string[];
  requirements: OrderRequirement[];
}) => {
  if (!otherFormats.length && !requirements.length) {
    return null;
  }
  return (
    <div className="space-y-4 rounded-lg border border-zinc-200 p-4 text-sm text-zinc-700">
      {!!otherFormats.length && (
        <div className="space-y-1">
          <h3 className="text-xs font-semibold text-zinc-800 uppercase">
            Also available in
          </h3>
          <p>{otherFormats.join(", ")}</p>
        </div>
      )}
      {!!requirements.length && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-zinc-800 uppercase">
            Order requirements
          </h3>
          {requirements.map((requirement) => (
            <ProductDialogOrderRequirement
              key={requirement.key}
              requirement={requirement}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ProductDialogOrderRequirement = ({
  requirement,
}: {
  requirement: OrderRequirement;
}) => {
  if (requirement.state === "description") {
    return <p>{requirement.description}</p>;
  }
  if (requirement.state === "met") {
    return (
      <p className="flex items-start gap-1 text-purple-700">
        <Icon icon="CHECK_CIRCLE" size={0.9} className="shrink-0" />
        <span>{requirement.metText}</span>
      </p>
    );
  }
  return (
    <div className="space-y-2 text-purple-700">
      <p>{requirement.remainingText}</p>
      <div
        role="progressbar"
        aria-label={requirement.description}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(
          100,
          Math.max(0, Math.round(requirement.percentageComplete)),
        )}
        className="h-1.5 overflow-hidden rounded-full bg-purple-100"
      >
        <div
          className="h-full rounded-full bg-purple-600"
          style={{
            width: `${Math.min(100, Math.max(0, requirement.percentageComplete))}%`,
          }}
        />
      </div>
    </div>
  );
};

const ProductDialogFooter = ({
  product,
  basketActions,
  onShowSupplier,
  onShopWithSupplier,
}: {
  product: DiscoveryProductDetails;
  basketActions: DiscoveryProductBasketActions;
  onShowSupplier: () => void;
  onShopWithSupplier: () => void;
}) => {
  const buttonClassName =
    "flex h-11 w-full cursor-pointer items-center justify-center gap-1 rounded-md bg-zinc-800 px-3 text-sm text-white hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:bg-zinc-300";

  if (product.variant === "non-sd") {
    return (
      <Button
        type="button"
        color="slim"
        className={buttonClassName}
        onClick={onShopWithSupplier}
      >
        Shop with {product.supplierName}
      </Button>
    );
  }

  if (!basketActions.canAddToBasket) {
    return (
      <Button
        type="button"
        color="slim"
        className={buttonClassName}
        onClick={onShowSupplier}
      >
        More from {product.supplierName}
      </Button>
    );
  }

  return (
    <>
      <span className="shrink-0 text-base font-semibold text-zinc-800">
        <ProductPricing price={product.sku.price} />
      </span>
      {basketActions.basketQuantity === 0 ? (
        <Button
          type="button"
          color="slim"
          aria-label={`Add ${product.name}`}
          className={buttonClassName}
          disabled={basketActions.basketActionDisabled}
          onClick={basketActions.onAddToBasket}
        >
          <Icon icon="PLUS" size={0.9} />
          Add
        </Button>
      ) : (
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Button
            type="button"
            color="slim"
            aria-label={`Remove one ${product.name}`}
            className={twMerge(buttonClassName, "w-12 shrink-0 p-0")}
            disabled={basketActions.basketActionDisabled}
            onClick={basketActions.onDecrementQuantityInBasket}
          >
            <Icon icon="MINUS" size={0.9} />
          </Button>
          <output
            aria-label={`Quantity of ${product.name} in basket`}
            aria-live="polite"
            className="flex h-11 min-w-0 flex-1 items-center justify-center rounded-md border border-zinc-200 text-base"
          >
            {basketActions.basketQuantity}
          </output>
          <Button
            type="button"
            color="slim"
            aria-label={`Add one ${product.name}`}
            className={twMerge(buttonClassName, "w-12 shrink-0 p-0")}
            disabled={basketActions.basketActionDisabled}
            onClick={basketActions.onIncrementQuantityInBasket}
          >
            <Icon icon="PLUS" size={0.9} />
          </Button>
        </div>
      )}
    </>
  );
};

const SkuProfitCalculator = ({ sku }: { sku: DiscoveryProductSku }) => {
  const [expanded, setExpanded] = useState(false);
  const [grossProfitMarginPercentage, setGrossProfitMarginPercentage] =
    useState(0);
  const id = useId();
  const profitDetails = useCalculateProfitDetails({
    grossProfitMarginPercentage,
    sku,
  });
  return (
    <div className="rounded-lg border border-zinc-200 text-sm">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={`${id}-content`}
        onClick={() => setExpanded((current) => !current)}
        className="flex min-h-11 w-full cursor-pointer items-center gap-2 px-3 py-2 text-left font-medium text-zinc-800"
      >
        <svg
          aria-hidden="true"
          className="size-4 shrink-0"
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
        >
          <rect x="4" y="2" width="12" height="16" rx="2" />
          <path d="M7 5h6v3H7zM7 11h1m4 0h1m-6 3h1m4 0h1" />
        </svg>
        <span className="flex-1">Calculate gross profit margin</span>
        <Icon icon={expanded ? "CHEVRON_UP" : "CHEVRON_RIGHT"} size={0.8} />
      </button>
      <div
        id={`${id}-content`}
        hidden={!expanded}
        className="space-y-3 px-3 pb-4"
      >
        <label
          className="flex items-center justify-between gap-3 border-b border-zinc-200 pb-3"
          htmlFor={`${id}-margin`}
        >
          <span>Gross profit margin (%)</span>
          <InputContainer className="h-10">
            <InputNumber
              id={`${id}-margin`}
              className="w-20"
              max={100}
              onChange={(event) =>
                setGrossProfitMarginPercentage(Number(event.target.value))
              }
              type="number"
              disabled={getAvailablePrice(sku.price) === undefined}
              defaultValue={grossProfitMarginPercentage}
            />
          </InputContainer>
        </label>
        <div className="flex items-center justify-between gap-4">
          <span className="text-zinc-500">Selling price</span>
          <span className="font-medium">
            <ProductPricing
              price={
                profitDetails
                  ? {
                      type: "fixed",
                      price: profitDetails.sellingPrice,
                      basePrice: null,
                      fullPrice: null,
                    }
                  : sku.price
              }
            />
          </span>
        </div>
        <div className="flex items-center justify-between gap-4">
          <span className="text-zinc-500">Profit after VAT</span>
          <span className="font-medium">
            <ProductPricing
              price={
                profitDetails
                  ? {
                      type: "fixed",
                      price: profitDetails.profitAfterVat,
                      basePrice: null,
                      fullPrice: null,
                    }
                  : sku.price
              }
            />
          </span>
        </div>
      </div>
    </div>
  );
};

const useCalculateProfitDetails = ({
  grossProfitMarginPercentage,
  sku,
}: {
  grossProfitMarginPercentage: number;
  sku: DiscoveryProductSku;
}): { sellingPrice: number; profitAfterVat: number } | undefined => {
  const price = getAvailablePrice(sku.price);
  if (price === undefined) {
    return undefined;
  }
  return estimateRetailServingPrice({
    priceExVat: price,
    vatPercentage: sku.supplierVatPercentage,
    grossProfitMarginPercentage,
    servings: getRetailServingDetails(sku).servings,
  });
};

const SearchResultsView = ({
  products,
  rawProductListQuery,
}: {
  products: ProductSearchCardProps[];
  rawProductListQuery: ReturnType<
    typeof useDiscoveryV3SearchProductListManager
  >["rawQuery"];
}) => {
  return (
    <>
      <div className="grid grid-cols-2 gap-3 pb-4 md:grid-cols-[repeat(auto-fill,minmax(190px,1fr))]">
        {products.map((product) => (
          <ProductSearchCard key={product.productItemId} {...product} />
        ))}
      </div>
      <IntersectingInfiniteLoadingButton query={rawProductListQuery} />
    </>
  );
};

const SearchCriterionLabel = ({
  criterion,
}: {
  criterion: AppliedCriterion;
}) => {
  if (criterion.kind === "search") {
    return (
      <span
        className="flex min-w-0 font-medium text-purple-600"
        title={criterion.label}
      >
        <span className="shrink-0">&quot;</span>
        <span className="truncate">{criterion.label}</span>
        <span className="shrink-0">&quot;</span>
      </span>
    );
  }

  return (
    <span
      className="min-w-0 truncate font-medium text-purple-600"
      title={criterion.label}
    >
      {criterion.label}
    </span>
  );
};

const RemoveSearchCriterionButton = ({
  criterion,
}: {
  criterion: AppliedCriterion;
}) => (
  <Button
    className={twMerge(
      "flex min-h-11 w-full cursor-pointer items-center justify-between gap-3 rounded-lg border border-zinc-200 bg-transparent px-4 py-2 text-left text-sm font-medium text-zinc-800 hover:bg-zinc-50/50",
      criterion.kind === "regular" && "capitalize",
    )}
    onClick={criterion.onRemove}
    type="button"
    color="slim"
  >
    <span className="flex min-w-0 items-center gap-1">
      <span className="shrink-0">Remove</span>
      <SearchCriterionLabel criterion={criterion} />
    </span>
    <Icon className="shrink-0 text-zinc-500" icon="CHEVRON_RIGHT" size={0.7} />
  </Button>
);

const NoSearchResultsView = ({
  appliedCriteria,
}: {
  appliedCriteria: AppliedCriterion[];
}) => {
  return (
    <div
      className={twMerge(
        "flex min-h-96 flex-col items-center justify-center gap-8 px-4 py-12 text-center",
        appliedCriteria.length > 0 && "pt-8",
      )}
    >
      <div className="flex flex-col items-center">
        <Icon className="mb-5 text-zinc-200" icon="MAGNIFY" size={1.33} />
        <h2 className="text-xl font-semibold text-zinc-800">
          No products found
        </h2>
        <p className="mt-2 max-w-80 text-sm font-normal text-zinc-500">
          Your current search doesn&apos;t match any products on the Market
        </p>
      </div>

      {appliedCriteria.length > 0 && (
        <div className="w-full max-w-96">
          <p className="mb-3 text-xs font-semibold tracking-wide text-zinc-500 uppercase">
            Try removing a filter
          </p>
          <div className="flex flex-col gap-4">
            {appliedCriteria.map((criterion) => (
              <RemoveSearchCriterionButton
                key={criterion.id}
                criterion={criterion}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const ProductOverlay = ({
  overlay,
}: {
  overlay: ProductOverlayData | undefined;
}) => {
  if (!overlay) return null;

  return (
    <span className="flex items-center gap-1 text-xs font-medium text-zinc-600">
      {overlay.type === "delivery-date" && (
        <Icon
          className="text-purple-700"
          icon="SELLAR_DISTRIBUTION_TRUCK"
          size={0.55}
        />
      )}
      {overlay.type === "available-from" && (
        <Icon className="text-blue-600" icon="PROGRESS_HELPER" size={0.55} />
      )}
      {overlay.type === "direct" && (
        <Icon className="text-zinc-600" icon="TRUCK" size={0.55} />
      )}
      {overlay.label}
    </span>
  );
};

const productSearchStockPresentation: Record<
  StockStatus,
  { label: string; textClassName: string; dotClassName: string }
> = {
  available: {
    label: "In stock",
    textClassName: "text-emerald-700",
    dotClassName: "bg-emerald-500",
  },
  "in-stock": {
    label: "In stock",
    textClassName: "text-emerald-700",
    dotClassName: "bg-emerald-500",
  },
  "low-stock": {
    label: "Low stock",
    textClassName: "text-yellow-700",
    dotClassName: "bg-yellow-500",
  },
  "out-of-stock": {
    label: "Sold out",
    textClassName: "text-red-700",
    dotClassName: "bg-red-500",
  },
};

const SearchCardStockIndicator = ({ status }: { status: StockStatus }) => {
  const presentation = productSearchStockPresentation[status];

  return (
    <span
      className={twMerge(
        "flex items-center gap-1 text-xs font-medium",
        presentation.textClassName,
      )}
    >
      <span
        aria-hidden="true"
        className={twMerge(
          "size-1.5 shrink-0 rounded-full",
          presentation.dotClassName,
        )}
      />
      {presentation.label}
    </span>
  );
};

const ProductSearchCardImage = ({
  imageUrls,
  badgeImageUrl,
  name,
}: Pick<ProductSearchCardProps, "imageUrls" | "badgeImageUrl" | "name">) => {
  const productImages = imageUrls.filter(Boolean);

  if (productImages.length > 1) {
    return (
      <ImageSlider
        hoverAnimate
        imageClassName="pt-[56.25%]"
        images={productImages}
      />
    );
  }

  if (!productImages.length && badgeImageUrl) {
    return (
      <>
        <CardImage
          className="h-full w-full rounded-none blur-lg"
          fallback="product-hero"
          imageUrl={badgeImageUrl}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src={badgeImageUrl}
            alt={name}
            width={100}
            height={100}
            className="size-25 rounded-full"
          />
        </div>
      </>
    );
  }

  return (
    <CardImage
      className="h-full w-full rounded-none"
      fallback="product-hero"
      imageUrl={productImages[0] ?? ""}
    />
  );
};

const ProductSearchCard = (props: ProductSearchCardProps) => {
  const {
    name,
    supplierName,
    style,
    abv,
    price,
    supplierLogoUrl,
    imageUrls = [],
    badgeImageUrl,
    dietaryTags = [],
    overlay,
  } = props;

  const handleClickCard = props.onViewProduct;

  const formattedAbv = abv !== undefined ? formatAbv(abv) : undefined;
  const productDetails = [style, formattedAbv].filter(Boolean).join(" · ");
  const { status: stockStatus } = getStockDisplay({
    initStock: props.availableStock,
    canBackorder: props.canBackorder,
    type: "full",
    showStock: false,
  });

  return (
    <>
      <article
        className="flex min-w-0 cursor-pointer flex-col overflow-hidden rounded-xl bg-white text-zinc-800 shadow-md/10 transition-shadow hover:shadow-lg/15"
        onClick={handleClickCard}
        onKeyDown={(event) => {
          if (
            event.target === event.currentTarget &&
            (event.key === "Enter" || event.key === " ")
          ) {
            event.preventDefault();
            handleClickCard();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className="relative aspect-video overflow-hidden bg-white">
          <ProductSearchCardImage
            imageUrls={imageUrls}
            badgeImageUrl={badgeImageUrl}
            name={name}
          />
        </div>

        <div className="grid min-w-0 flex-1 grid-cols-1 grid-rows-[1fr_auto] gap-5 p-3">
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex min-w-0 items-center gap-1 text-xs font-medium text-zinc-600">
              <CardImage
                className="size-4 shrink-0 rounded-full"
                fallback="supplier-badge"
                imageUrl={supplierLogoUrl}
              />
              <span className="min-w-0 flex-1 truncate" title={supplierName}>
                {supplierName}
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <h2 className="line-clamp-2 text-base font-semibold text-zinc-800">
                {name}
              </h2>
              <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 text-sm font-normal text-zinc-800">
                {productDetails && (
                  <span className="line-clamp-2 min-w-0">{productDetails}</span>
                )}
                {!!dietaryTags.length && (
                  <>
                    {productDetails && <span className="text-zinc-400">•</span>}
                    <DietaryTags tags={dietaryTags} />
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-semibold">{props.formatLabel}</p>
              <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 text-xs font-medium">
                <SearchCardStockIndicator status={stockStatus} />
                <ProductOverlay overlay={overlay} />
              </div>
            </div>
            <div className="flex flex-col items-start gap-3 border-t border-zinc-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="shrink-0 text-base font-bold whitespace-nowrap">
                <ProductPricing price={price} />
              </div>
              <div className="flex w-full flex-1 md:max-w-[60%]">
                {props.canAddToBasket ? (
                  <ProductSearchCardBasketAction {...props} />
                ) : (
                  <Button
                    color="slim"
                    className="flex h-9 w-full items-center justify-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 text-sm font-medium shadow-sm"
                    onClick={(event) => {
                      event.stopPropagation();
                      props.onViewProduct();
                    }}
                    type="button"
                  >
                    View
                    <Icon
                      icon="CHEVRON_RIGHT"
                      size={0.7}
                      className="relative top-px shrink-0"
                    />
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </article>
    </>
  );
};

const ProductSearchCardBasketAction = (props: ProductSearchCardProps) => {
  const { name, basketActionDisabled } = props;
  if (props.basketQuantity) {
    return (
      <div className="flex flex-1 gap-1">
        <Button
          color="slim"
          aria-label={`Remove one ${name}`}
          className="flex h-9 w-9 shrink-0 items-center justify-center border border-zinc-800 bg-zinc-800 text-white shadow-[0_2px_2px_0_rgba(0,0,0,0.15)] hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:bg-zinc-300"
          disabled={basketActionDisabled}
          onClick={(event) => {
            event.stopPropagation();
            props.onDecrementQuantityInBasket();
          }}
          type="button"
        >
          <Icon icon="MINUS" size={0.8} />
        </Button>
        <output className="flex h-9 w-9 flex-1 items-center justify-center rounded-md border border-zinc-200 text-sm">
          {props.basketQuantity}
        </output>
        <Button
          color="slim"
          aria-label={`Add one ${name}`}
          className="flex h-9 w-9 shrink-0 items-center justify-center border border-zinc-800 bg-zinc-800 text-white shadow-[0_2px_2px_0_rgba(0,0,0,0.15)] hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:bg-zinc-300"
          disabled={basketActionDisabled}
          onClick={(event) => {
            event.stopPropagation();
            props.onIncrementQuantityInBasket();
          }}
          type="button"
        >
          <Icon icon="PLUS" size={0.8} />
        </Button>
      </div>
    );
  }

  return (
    <Button
      className="flex h-9 w-full items-center justify-center gap-1 rounded-md border border-zinc-800 bg-zinc-800 px-2 text-sm text-white capitalize shadow-[0_2px_2px_0_rgba(0,0,0,0.15)] hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:bg-zinc-300"
      disabled={basketActionDisabled}
      onClick={(event) => {
        event.stopPropagation();
        props.onAddToBasket();
      }}
      type="button"
      color="slim"
    >
      <Icon icon="PLUS" size={0.75} />
      Add
    </Button>
  );
};

const DietaryTags = ({
  tags,
  variant = "plain",
}: {
  tags: ProductDietaryTag[];
  variant?: "badge" | "plain";
}) => {
  if (!tags.length) return null;

  return (
    <div className="flex shrink-0 gap-1 text-[10px] font-medium">
      {tags.map((tag) => (
        <span
          key={tag}
          className={twMerge(
            dietaryTagLabels[tag].className,
            variant === "badge" &&
              "flex size-7 items-center justify-center rounded-full",
            variant === "badge" && dietaryTagLabels[tag].badgeClassName,
          )}
          title={dietaryTagLabels[tag].label}
        >
          {dietaryTagLabels[tag].shortLabel}
        </span>
      ))}
    </div>
  );
};

const dietaryTagLabels: Record<
  ProductDietaryTag,
  {
    label: string;
    shortLabel: string;
    className: string;
    badgeClassName: string;
  }
> = {
  vegan: {
    label: "Vegan",
    shortLabel: "V",
    className: "text-emerald-800",
    badgeClassName: "bg-emerald-100",
  },
  "lactose-free": {
    label: "Lactose free",
    shortLabel: "LF",
    className: "text-blue-800",
    badgeClassName: "bg-blue-100",
  },
  "gluten-free": {
    label: "Gluten free",
    shortLabel: "GF",
    className: "text-yellow-800",
    badgeClassName: "bg-yellow-100",
  },
};

const AppliedFiltersRow = ({
  filters,
  onClearAll,
}: {
  filters: AppliedCriterion[];
  onClearAll: () => void;
}) => {
  if (!filters.length) return null;

  return (
    <div className="flex min-w-0 scrollbar-none flex-nowrap items-center gap-2 overflow-x-auto pb-0.5 md:flex-wrap md:overflow-x-visible md:pb-0">
      {filters.map((filter) => (
        <button
          key={filter.id}
          className="flex shrink-0 cursor-pointer items-center gap-1 rounded-full border-[0.74px] border-[#BA7BDD] bg-[#BA7BDD33] px-3 py-1.5 text-sm font-medium whitespace-nowrap text-[#8C23C7] hover:bg-purple-200"
          onClick={filter.onRemove}
          type="button"
        >
          <span>{filter.label}</span>
          <Icon icon="CLOSE" size={0.55} />
        </button>
      ))}
      <button
        className="shrink-0 cursor-pointer px-1 py-1.5 text-xs whitespace-nowrap text-gray-500 hover:text-gray-700"
        onClick={onClearAll}
        type="button"
      >
        Clear all
      </button>
    </div>
  );
};

const FilterContainer = ({
  title,
  children,
  defaultExpanded = false,
  isApplied,
  onClear,
}: {
  title: string;

  children: ReactNode;
  defaultExpanded?: boolean;
  isApplied: boolean;
  onClear: () => void;
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <section className="py-4">
      <div className="flex items-center justify-between gap-2">
        <button
          aria-expanded={expanded}
          className="flex min-w-0 flex-1 cursor-pointer items-center justify-between gap-2 text-left"
          onClick={() => setExpanded((current) => !current)}
          type="button"
        >
          <span
            className={twMerge(
              "text-xs font-medium tracking-wide uppercase",
              isApplied ? "text-zinc-800" : "text-gray-500",
            )}
          >
            {title}
          </span>
        </button>
        {isApplied && (
          <button
            className="flex cursor-pointer text-xs text-purple-600 hover:text-purple-700"
            onClick={onClear}
            type="button"
          >
            Clear
          </button>
        )}
        <Icon
          className="shrink-0 cursor-pointer text-gray-500"
          icon={expanded ? "CHEVRON_UP" : "CHEVRON_DOWN"}
          size={0.75}
          onClick={() => setExpanded((current) => !current)}
        />
      </div>

      {expanded && <div className="pt-3">{children}</div>}
    </section>
  );
};

const NestedCheckboxSelector = <T extends string>({
  options,
  secondaryOptions = [],
  selectedValues,
  onChange,
}: {
  options: readonly NestedCheckboxOption<T>[];
  secondaryOptions?: readonly NestedCheckboxOption<T>[];
  selectedValues: readonly T[];
  onChange: (selectedValues: T[]) => void;
}) => {
  const [secondaryOptionsExpanded, setSecondaryOptionsExpanded] =
    useState(false);
  const visibleOptions = secondaryOptionsExpanded
    ? [...options, ...secondaryOptions]
    : options;

  const toggleOption = (
    option: NestedCheckboxOption<T>,
    ancestorValues: readonly T[],
  ) => {
    const optionValues = getOptionValues(option);
    const isSelected = optionValues.some((value) =>
      selectedValues.includes(value),
    );

    if (!isSelected) {
      const valuesToReplace = [...ancestorValues, ...optionValues];
      onChange([
        ...selectedValues.filter(
          (selectedValue) => !valuesToReplace.includes(selectedValue),
        ),
        option.value,
      ]);
      return;
    }

    onChange(
      selectedValues.filter(
        (selectedValue) => !optionValues.includes(selectedValue),
      ),
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex max-h-64 flex-col gap-2 overflow-y-auto pr-3">
        {visibleOptions.map((option) => (
          <CheckboxOption
            key={option.value}
            option={option}
            selectedValues={selectedValues}
            toggleOption={toggleOption}
          />
        ))}
      </div>
      {secondaryOptions.length > 0 && (
        <button
          aria-expanded={secondaryOptionsExpanded}
          className="flex cursor-pointer items-center gap-1 self-start text-sm text-purple-600 hover:text-purple-700"
          onClick={() => setSecondaryOptionsExpanded((current) => !current)}
          type="button"
        >
          {secondaryOptionsExpanded ? "Less" : "More"}
          <Icon
            icon={secondaryOptionsExpanded ? "CHEVRON_UP" : "CHEVRON_RIGHT"}
            size={0.75}
          />
        </button>
      )}
    </div>
  );
};

const CheckboxOption = <T extends string>({
  option,
  selectedValues,
  toggleOption,
  ancestorValues = [],
  depth = 0,
}: {
  option: NestedCheckboxOption<T>;
  selectedValues: readonly T[];
  toggleOption: (
    option: NestedCheckboxOption<T>,
    ancestorValues: readonly T[],
  ) => void;
  ancestorValues?: readonly T[];
  depth?: number;
}) => {
  const isSelected = getOptionValues(option).some((value) =>
    selectedValues.includes(value),
  );

  return (
    <div>
      <label
        className="flex cursor-pointer items-center gap-2 text-sm text-zinc-700"
        style={{ paddingLeft: depth * 16 }}
      >
        <InputCheckbox
          checked={isSelected}
          className="h-4 w-4 rounded-xs bg-white accent-purple-600 [&:not(:checked)]:appearance-none [&:not(:checked)]:border-zinc-200"
          onChange={() => toggleOption(option, ancestorValues)}
        />
        <span>{option.label}</span>
      </label>
      {isSelected && !!option.children?.length && (
        <div className="mt-2 flex flex-col gap-2">
          {option.children.map((child) => (
            <CheckboxOption
              key={child.value}
              ancestorValues={[...ancestorValues, option.value]}
              depth={depth + 1}
              option={child}
              selectedValues={selectedValues}
              toggleOption={toggleOption}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const getOptionValues = <T extends string>(
  option: NestedCheckboxOption<T>,
): T[] => [
  option.value,
  ...(option.children?.flatMap((child) => getOptionValues(child)) ?? []),
];

const getOptionLabel = <T extends string>(
  options: readonly NestedCheckboxOption<T>[],
  value: T,
): string => findOption(options, value)?.label ?? value;

const findOption = <T extends string>(
  options: readonly NestedCheckboxOption<T>[],
  value: T,
): NestedCheckboxOption<T> | undefined => {
  for (const option of options) {
    if (option.value === value) return option;

    const childOption = option.children
      ? findOption(option.children, value)
      : undefined;
    if (childOption) return childOption;
  }
};

const removeOption = <T extends string>(
  selectedValues: readonly T[],
  value: T,
  options?: readonly NestedCheckboxOption<T>[],
): T[] => {
  const option = options ? findOption(options, value) : undefined;
  const valuesToRemove = option ? getOptionValues(option) : [value];

  return selectedValues.filter(
    (selectedValue) => !valuesToRemove.includes(selectedValue),
  );
};

const MobileSearchControls = ({
  appliedFilterCount,
  onOpenFilters,
}: {
  appliedFilterCount: number;
  onOpenFilters: () => void;
}) => (
  <div className="bg-neutral-100 px-4 lg:hidden">
    <div className="flex min-h-16.5 items-center justify-between py-3">
      <Button
        className="gap-2 text-sm shadow-[0px_2px_2px_0px_rgba(0,0,0,0.15)]"
        color="slim"
        onClick={onOpenFilters}
        size="small"
      >
        <Icon icon="FILTER_VARIANT" size={0.75} />
        Filters
        <span className="flex h-5.5 w-5.5 items-center justify-center rounded-full bg-[#8C23C7] text-white">
          {appliedFilterCount}
        </span>
      </Button>
      <OrderBySelector
        label="Sort:"
        labelClassName="text-zinc-500"
        className="gap-1.5 rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm leading-4.5 font-medium shadow-[0px_2px_2px_0px_rgba(0,0,0,0.15)] hover:bg-zinc-100 hover:stroke-zinc-300 hover:text-zinc-800"
      />
    </div>
  </div>
);

const SearchHeader = ({
  appliedFilterCount,
  onOpenFilters,
  searchValue,
  basketEnabled,
  basketQuantity,
  onSubmitSearch,
  onCommitSearch,
  onOpenBasket,
  onClickLeftIcon,
}: {
  appliedFilterCount: number;
  onOpenFilters: () => void;
  searchValue: string;
  basketEnabled: boolean;
  basketQuantity: number;
  onOpenBasket: () => void;
  onSubmitSearch: (searchValue: string) => void;
  onCommitSearch: (label: string) => void;
  onClickLeftIcon: () => void;
}) => {
  return (
    <>
      <NavigationBar
        borderBottom={false}
        leftIcon="CHEVRON_LEFT"
        onClickLeftIcon={onClickLeftIcon}
        title="Search results"
        rightComponent={
          basketEnabled ? (
            <MarketBasketFlyover onViewBasket={onOpenBasket}>
              <button
                className="relative flex cursor-pointer items-center"
                onClick={onOpenBasket}
                type="button"
              >
                <Icon icon="CART_OUTLINE" size={1} />
                {basketQuantity > 0 && (
                  <QuantityIndicator
                    className="absolute -top-2 -right-2 bg-[#A34FD2]"
                    quantity={basketQuantity}
                  />
                )}
              </button>
            </MarketBasketFlyover>
          ) : undefined
        }
      />
      <NavigationBarSubHeader className="border-b-0 lg:border-b">
        <div className="mx-auto max-w-300 p-4">
          <DiscoveryMarketSearch
            fullWidth
            onCommit={(selection) => onCommitSearch(selection.label)}
            initialValue={searchValue}
            onSubmit={(searchValue) => {
              onSubmitSearch(searchValue);
            }}
            placeholder="Search for products or suppliers..."
            inputClassName="placeholder:text-sm placeholder:font-normal"
          />
        </div>
        <MobileSearchControls
          appliedFilterCount={appliedFilterCount}
          onOpenFilters={onOpenFilters}
        />
      </NavigationBarSubHeader>
    </>
  );
};

const formatAbv = (abv: number) => {
  return `${abv}%`;
};

const formatProductPrice = (productPrice: AvailableProductPrice) => {
  switch (productPrice.type) {
    case "starting-at":
      return `from ${formatCurrency(productPrice.price)}`;
    case "fixed":
      return formatCurrency(productPrice.price);
  }
};

const getDeliveryWindowRanges = (): Record<
  Exclude<DeliveredBySellarFilterOption, "all">,
  { low: Date; high: Date }
> => {
  const today = dayjs().startOf("day");
  const daysUntilFriday = (5 - today.day() + 7) % 7;
  const weekendEnd = today.add(daysUntilFriday, "day").endOf("day");
  const nextWeekStart = weekendEnd.add(1, "millisecond");
  const nextWeekEnd = weekendEnd.add(7, "day");
  const weekAfterNextStart = nextWeekEnd.add(1, "millisecond");
  const weekAfterNextEnd = weekendEnd.add(14, "day");

  return {
    weekend: { low: today.toDate(), high: weekendEnd.toDate() },
    "next-week": {
      low: nextWeekStart.toDate(),
      high: nextWeekEnd.toDate(),
    },
    "week-after-next": {
      low: weekAfterNextStart.toDate(),
      high: weekAfterNextEnd.toDate(),
    },
  };
};

type UseDiscoveryTrackingManagerProps = {
  isDeliveredBySellar: boolean;
  selectedSupplierCount: number;
  selectedDeliveredBySellarOption: DeliveredBySellarFilterOption;
  selectedCutOffAt: Date | undefined;
  productListQueryLoading: boolean;
  productListResultCount: number;
};

const useDiscoveryTrackingManager = ({
  isDeliveredBySellar,
  selectedSupplierCount,
  selectedDeliveredBySellarOption,
  selectedCutOffAt,
  productListQueryLoading,
  productListResultCount,
}: UseDiscoveryTrackingManagerProps) => {
  const { sessionId, journeySource } = useMarketStorefrontSessionId();
  const { track } = useEvents();

  const {
    onSearchSubmitted: trackSearchSubmittedForIntercom,
    onFilterChanged: trackSearchFilterChangedForIntercom,
  } = useConsolidatedOrderingSearchIntercom({
    sessionId,
    journeySource,
    isDeliveredBySellar,
    isLoading: productListQueryLoading,
    resultCount: productListResultCount,
    selectedSupplierCount,
    deliveryFilter: selectedDeliveredBySellarOption,
    orderCutoffAt: selectedCutOffAt?.toISOString() ?? undefined,
  });

  const trackSearchViewed = () => {
    track("Delivered By Sellar - Search Viewed", {
      ...(sessionId ? { sessionId } : {}),
      journeySource,
      deliveryFilter: selectedDeliveredBySellarOption,
      ...(selectedCutOffAt ? { orderCutoffAt: selectedCutOffAt } : {}),
      selectedSupplierCount,
      resultCount: productListResultCount,
    });
  };

  const trackSearchSubmitted = (searchValue: string) => {
    trackSearchSubmittedForIntercom(searchValue);
    track("Discover term searched", {
      text: searchValue,
      ...(sessionId ? { sessionId } : {}),
      isDeliveredBySellar,
      resultCount: productListResultCount,
      ...getSellarWebsiteUtms(),
    });
  };

  const trackSearchFilterChanged = () => {
    trackSearchFilterChangedForIntercom();
  };

  return {
    trackSearchViewed,
    trackSearchSubmitted,
    trackSearchFilterChanged,
  };
};

const getAvailablePrice = (price: ProductPrice): number | undefined => {
  if (price.type === "fixed" || price.type === "starting-at") {
    return price.price;
  }
  return undefined;
};

const ProductPricing = ({ price }: { price: ProductPrice }) => {
  if (price.type === "fixed" || price.type === "starting-at") {
    return <>{formatProductPrice(price)}</>;
  }
  const message =
    price.type === "on-request"
      ? "Pricing on request"
      : "Sign in to view pricing";

  return (
    <Tooltip>
      <TooltipTrigger
        tabIndex={0}
        aria-label={message}
        className="inline-flex items-center gap-2"
      >
        <span aria-hidden="true">£</span>
        <div aria-hidden="true">
          <SkeletonContainer>
            <SkeletonText className="h-6 w-18" />
          </SkeletonContainer>
        </div>
      </TooltipTrigger>
      <TooltipContent className="rounded-md bg-black px-3 py-2 text-sm text-white">
        {message}
      </TooltipContent>
    </Tooltip>
  );
};

const toDiscoveryProductDetails = ({
  parentProduct,
  productItem,
  variant,
  deliveryEstimate,
  canRequestPricing,
  otherFormats,
}: {
  parentProduct: BuyerProductGetResponseType;
  productItem: BuyerProductGetResponseType["Products"][number];
  variant: ProductVariant;
  deliveryEstimate: DiscoveryProductDetails["deliveryEstimate"];
  canRequestPricing: boolean;
  otherFormats: string[];
}): DiscoveryProductDetails => {
  const [name = "", formatLabel = ""] = productItem.name?.split(" • ") ?? [];
  const price = getDiscoveryProductPrice(
    productItem.ProductPriceTier,
    canRequestPricing,
  );
  const dietaryTags: ProductDietaryTag[] = [
    parentProduct.vegan && "vegan",
    parentProduct.lactoseFree && "lactose-free",
    parentProduct.glutenFree && "gluten-free",
  ].filter(Boolean);

  return {
    productItemId: productItem.id,
    parentProductId: parentProduct.id,
    supplierId: parentProduct.SupplierId,
    supplierUrlId: parentProduct.Supplier.urlId,
    name,
    formatLabel,
    price,
    variant,
    availableStock: productItem.availableStock,
    canBackorder: productItem.canBackorder,
    style: parentProduct.kind ?? undefined,
    abv: parentProduct.abv ?? undefined,
    description: parentProduct.description ?? "",
    supplierName: parentProduct.Supplier.Business.name,
    supplierLogoUrl: parentProduct.Supplier.Business.logoUrl ?? "",
    badgeImageUrl: parentProduct.imageUrl ?? undefined,
    dietaryTags,
    deliveryEstimate,
    overlay: getDiscoveryProductOverlay({
      product: { availableFrom: productItem.availableFrom },
      deliveredBySellarFilterApplied: variant === "sd",
      deliveryEstimate,
    }),
    imageUrls: [
      parentProduct.imageUrl || parentProduct.Parent?.imageUrl,
      ...parentProduct.ProductImages.map((image) => image.url),
    ].filter(Boolean),
    heroImageUrl:
      parentProduct.heroImageUrl || parentProduct.Parent?.heroImageUrl || "",
    otherFormats,
    sku: {
      id: productItem.id,
      name,
      price,
      volume: productItem.volume ?? undefined,
      unit: productItem.unit ?? undefined,
      formatLabel,
      containerType: productItem.containerType ?? undefined,
      packQuantity: productItem.packQuantity ?? undefined,
      supplierVatPercentage: parentProduct.Supplier.vatPercentage,
      volumeDiscountTiers: [],
      variant,
    },
  };
};

const getDiscoveryProductOverlay = ({
  product,
  deliveredBySellarFilterApplied,
  deliveryEstimate,
}: {
  product: Pick<DiscoveryV2ListResponseType[number], "availableFrom">;
  deliveredBySellarFilterApplied: boolean;
  deliveryEstimate: DiscoveryProductDetails["deliveryEstimate"];
}): ProductOverlayData | undefined => {
  const isDeliveredBySellarFilterApplied = deliveredBySellarFilterApplied;

  if (
    isDeliveredBySellarFilterApplied &&
    deliveryEstimate &&
    dayjs(deliveryEstimate.orderCutoffDate).isAfter(dayjs())
  ) {
    return {
      type: "delivery-date",
      label: dayjs(deliveryEstimate.deliveryDate).format("ddd DD"),
    };
  }

  if (product.availableFrom && dayjs(product.availableFrom).isAfter(dayjs())) {
    return {
      type: "available-from",
      label: dayjs(product.availableFrom).format("DD/MM/YY"),
    };
  }

  return undefined;
};

const getDiscoveryProductDeliveryEstimate = (
  product: DiscoveryV2ListResponseType[number],
) => {
  if (product.matchingDeliveryDate && product.matchingOrderCutoffDate) {
    return {
      deliveryDate: product.matchingDeliveryDate,
      orderCutoffDate: product.matchingOrderCutoffDate,
    };
  }

  if (product.earliestDeliveryDate && product.earliestOrderCutoffDate) {
    return {
      deliveryDate: product.earliestDeliveryDate,
      orderCutoffDate: product.earliestOrderCutoffDate,
    };
  }

  return undefined;
};

const getDiscoveryProductPrice = (
  pricing:
    | {
        price: number | null | undefined;
        basePrice: number | null;
        fullPrice: number | null;
      }
    | undefined,
  canRequestPricing: boolean,
): ProductPrice => {
  if (pricing?.price !== null && pricing?.price !== undefined) {
    return {
      type: "fixed",
      price: pricing.price,
      basePrice: pricing.basePrice,
      fullPrice: pricing.fullPrice,
    };
  }

  if (canRequestPricing) {
    return { type: "on-request" };
  }

  return { type: "hidden" };
};

const useDiscoveryProductSelection = () => {
  const { productItemId, parentProductId, setSelectedProductIds } =
    useDiscoveryV2SearchStore(
      useShallow((state) => ({
        productItemId: state.selectedProductId,
        parentProductId: state.selectedProductParentId,
        setSelectedProductIds: state.setSelectedProductIds,
      })),
    );

  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    return () => {
      setSelectedProductIds(undefined);
    };
  }, [setSelectedProductIds]);

  const selectedProduct: DiscoveryProductSelection | undefined =
    productItemId === undefined
      ? undefined
      : { productItemId, parentProductId };

  return {
    selectedProduct,
    isProductDialogOpen: productItemId !== undefined && !isClosing,
    openProductDialog: (product: DiscoveryProductSelection) => {
      setIsClosing(false);
      setSelectedProductIds(product);
    },
    closeProductDialog: () => setIsClosing(true),
    clearProductSelection: () => {
      setSelectedProductIds(undefined);
      setIsClosing(false);
    },
  };
};

const useDiscoveryProductDeliveryEstimate = ({
  supplierId,
  productItemId,
  searchProduct,
  enabled,
}: {
  supplierId: number;
  productItemId: number;
  searchProduct: DiscoveryV2ListResponseType[number] | undefined;
  enabled: boolean;
}) => {
  const { buyerPostcode } = useStorefrontBuyerPostcode();
  const shouldFetchEstimate = Boolean(
    enabled && !searchProduct && supplierId && buyerPostcode,
  );
  const deliveryEstimateQuery =
    useSellarDistributionAvailabilityDeliveryEstimates(
      {
        query: [
          { type: "supplierId", modifier: "EQUALS", value: supplierId },
          { type: "productIds", modifier: "EQUALS", value: [productItemId] },
          {
            type: "retailerPostcode",
            modifier: "EQUALS",
            value: buyerPostcode,
          },
        ],
      },
      { enabled: shouldFetchEstimate },
    );

  const deliveryEstimate = resolveDiscoveryProductDeliveryEstimate({
    searchProduct,
    fetchedEstimate: deliveryEstimateQuery.data,
    shouldUseFetchedEstimate: shouldFetchEstimate,
  });

  return {
    deliveryEstimate,
    isLoading: shouldFetchEstimate && deliveryEstimateQuery.isPending,
    isError: shouldFetchEstimate && deliveryEstimateQuery.isError,
    reloadDeliveryEstimate: () => {
      if (shouldFetchEstimate) {
        return deliveryEstimateQuery.refetch();
      }
    },
  };
};

const useDiscoveryProductBasketActions = ({
  product,
  basket,
  canAddToBasket,
}: {
  product: ConsolidatedBasketProduct | undefined;
  basket: ReturnType<typeof useConsolidatedBasket>;
  canAddToBasket: boolean;
}): DiscoveryProductBasketActions => {
  const basketQuantity = product ? basket.getItemQuantity(product.id) : 0;

  return {
    basketQuantity,
    canAddToBasket,
    basketActionDisabled:
      !product ||
      !basket.isItemEnabled(product) ||
      basket.isItemMutating(product.id),
    onAddToBasket: () => {
      if (product) {
        return basket.addItemToBasket(product);
      }
    },
    onIncrementQuantityInBasket: () => {
      if (product) {
        return basket.updateItemQuantity(product, basketQuantity + 1);
      }
    },
    onDecrementQuantityInBasket: () => {
      if (product) {
        return basket.updateItemQuantity(product, basketQuantity - 1);
      }
    },
  };
};

type UseDiscoveryProductDialogOptions = {
  selectedProduct: DiscoveryProductSelection;
  searchProduct: DiscoveryV2ListResponseType[number] | undefined;
  isDeliveredBySellarSearch: boolean;
  basket: ReturnType<typeof useConsolidatedBasket>;
  onDismiss: () => void;
  onShowSupplierProducts: (supplier: { id: number; name: string }) => void;
};

type DiscoveryProductDialogState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; product: DiscoveryProductDetails };

const useDiscoveryProductDialog = ({
  selectedProduct,
  searchProduct,
  isDeliveredBySellarSearch,
  basket,
  onDismiss,
  onShowSupplierProducts,
}: UseDiscoveryProductDialogOptions) => {
  const { productItemId, parentProductId } = selectedProduct;
  const navigate = useNavigate();
  const { business } = useSellarCurrentBusiness();
  const { user } = useSellarUser();
  const consolidatedCheckoutEnabled = useRemoteFeatureFlags().isActive(
    "sdConsolidatedCheckout",
  );

  const canUseConsolidatedBasket = Boolean(
    user && business?.Retailer?.id && consolidatedCheckoutEnabled,
  );

  const parentProductQuery = useProductForMarket(parentProductId ?? 0, {
    enabled: !!parentProductId,
  });

  const parentProduct = parentProductQuery.data;

  const productItem = parentProduct?.Products.find(
    (product) => product.id === productItemId,
  );

  const supplierId = parentProduct?.SupplierId ?? 0;

  const otherFormats = useProductAvailableFormats(parentProduct, productItemId);

  const delivery = useDiscoveryProductDeliveryEstimate({
    supplierId,
    productItemId,
    searchProduct,
    enabled: Boolean(isDeliveredBySellarSearch && parentProduct && productItem),
  });

  const variant: ProductVariant =
    isDeliveredBySellarSearch && delivery.deliveryEstimate ? "sd" : "non-sd";

  const storefrontQuery = useSupplierStorefrontV2(
    supplierId,
    {},
    { enabled: !!supplierId && variant === "non-sd" },
  );

  const orderRequirements = useProductDialogOrderRequirements({
    supplierId,
    storefront: storefrontQuery.data,
    currency: parentProduct?.Supplier.currency ?? "gbp",
    basketEnabled: variant === "non-sd" && canUseConsolidatedBasket,
  });

  const { productDetails, basketProduct } = buildDiscoveryProductDialogData({
    parentProduct,
    productItem,
    delivery,
    variant,
    canRequestPricing: Boolean(business && user),
    otherFormats,
    priceIsFromConnection: searchProduct?.priceIsFromConnection ?? false,
  });

  const basketActions = useDiscoveryProductBasketActions({
    product: basketProduct,
    basket,
    canAddToBasket: variant === "sd" && canUseConsolidatedBasket,
  });

  const state = getDiscoveryProductDialogState({
    productDetails,
    isParentProductIdMissing: !parentProductId,
    isParentProductError: parentProductQuery.isError,
    isProductItemMissing: Boolean(parentProduct && !productItem),
    isDeliveryEstimateError: delivery.isError,
  });

  const reloadProduct = async () => {
    if (!parentProductId) {
      return;
    }
    await Promise.all([
      parentProductQuery.refetch(),
      delivery.reloadDeliveryEstimate(),
    ]);
  };

  const visitSupplierStorefront = () => {
    if (!productDetails) {
      return;
    }
    navigate(
      `${ORDER_BUYER.href({ supplierIdOrUrlId: productDetails.supplierUrlId })}?categoryId=${productDetails.parentProductId}`,
    );
  };

  const viewSupplierProducts = () => {
    if (productDetails?.variant === "sd") {
      onShowSupplierProducts({
        id: productDetails.supplierId,
        name: productDetails.supplierName,
      });
      onDismiss();
      return;
    }
    visitSupplierStorefront();
  };

  return {
    state,
    title: searchProduct?.name ?? "Product details",
    basketActions,
    orderRequirements,
    reloadProduct,
    viewSupplierProducts,
    visitSupplierStorefront,
  };
};

const getDiscoveryProductDialogState = ({
  productDetails,
  isParentProductIdMissing,
  isParentProductError,
  isProductItemMissing,
  isDeliveryEstimateError,
}: {
  productDetails: DiscoveryProductDetails | undefined;
  isParentProductIdMissing: boolean;
  isParentProductError: boolean;
  isProductItemMissing: boolean;
  isDeliveryEstimateError: boolean;
}): DiscoveryProductDialogState => {
  if (productDetails) {
    return { status: "ready", product: productDetails };
  }

  if (
    isParentProductIdMissing ||
    isParentProductError ||
    isProductItemMissing ||
    isDeliveryEstimateError
  ) {
    return { status: "error" };
  }

  return { status: "loading" };
};

const buildDiscoveryProductDialogData = ({
  parentProduct,
  productItem,
  delivery,
  variant,
  canRequestPricing,
  otherFormats,
  priceIsFromConnection,
}: {
  parentProduct: BuyerProductGetResponseType | undefined;
  productItem: BuyerProductGetResponseType["Products"][number] | undefined;
  delivery: {
    deliveryEstimate: ProductDeliveryEstimate | undefined;
    isLoading: boolean;
    isError: boolean;
  };
  variant: ProductVariant;
  canRequestPricing: boolean;
  otherFormats: string[];
  priceIsFromConnection: boolean;
}): {
  productDetails: DiscoveryProductDetails | undefined;
  basketProduct: ConsolidatedBasketProduct | undefined;
} => {
  if (
    !parentProduct ||
    !productItem ||
    delivery.isLoading ||
    delivery.isError
  ) {
    return { productDetails: undefined, basketProduct: undefined };
  }

  return {
    productDetails: toDiscoveryProductDetails({
      parentProduct,
      productItem,
      variant,
      deliveryEstimate: delivery.deliveryEstimate,
      canRequestPricing,
      otherFormats,
    }),
    basketProduct: {
      ...productItem,
      price: productItem.ProductPriceTier?.price ?? null,
      priceIsFromConnection,
      Parent: parentProduct,
      Supplier: parentProduct.Supplier,
      matchingDeliveryDate: delivery.deliveryEstimate?.deliveryDate ?? null,
      matchingOrderCutoffDate:
        delivery.deliveryEstimate?.orderCutoffDate ?? null,
      earliestDeliveryDate: null,
      earliestOrderCutoffDate: null,
    },
  };
};

const getSelectedDiscoveryStyles = ({
  styles,
  categoryKey,
}: {
  styles: string[];
  categoryKey: keyof typeof filterStyles | undefined;
}): string[] => {
  if (styles.length || !categoryKey) {
    return styles;
  }

  return [filterStyles[categoryKey].searchValue];
};

const resolveDiscoveryProductDeliveryEstimate = ({
  searchProduct,
  fetchedEstimate,
  shouldUseFetchedEstimate,
}: {
  searchProduct: DiscoveryV2ListResponseType[number] | undefined;
  fetchedEstimate:
    | SellarDistributionAvailabilityDeliveryEstimatesResponseType
    | undefined;
  shouldUseFetchedEstimate: boolean;
}): ProductDeliveryEstimate | undefined => {
  if (
    shouldUseFetchedEstimate &&
    fetchedEstimate?.nextDeliveryAt &&
    fetchedEstimate.orderBy
  ) {
    return {
      deliveryDate: dayjs(fetchedEstimate.nextDeliveryAt).toISOString(),
      orderCutoffDate: dayjs(fetchedEstimate.orderBy).toISOString(),
    };
  }

  if (searchProduct) {
    return getDiscoveryProductDeliveryEstimate(searchProduct);
  }

  return undefined;
};
